import {makeOwnDisplayName} from './services/portfolio-groups';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    ViewChild
} from '@angular/core';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {
    AdjustmentPricingSettingsDto,
    GetAdjustmentSolutions,
    SetImpliedVolatilityShell,
    UpdateTrackingObjectOnSweetPriceSettingsUpdate,
    UpdateTrackingObjectsOnTemplateUpdate
} from '../shell-communication/shell-operations-protocol';
import {
    arraysEqual,
    balanceStatePositions,
    delay,
    DetectMethodChanges,
    DetectSetterChanges,
    DxValueChanged,
    findHCF,
    getAvailableCashFlowStrategies,
    getShortUUID,
    getStrikeStep,
    isNullOrUndefined,
    isReversedCashFlowOrder,
    isValidNumber,
    isVoid,
    makeFullExpirationDate,
    makeGuiFriendlyExpirationDate,
    makePortfolioKey,
    removeFromArray
} from '../utils';
// CR-20260904-001, Zones Z7 (contract C4): the current-zone query the ORION Zone view reads.
import {
    GetPortfolioCurrentZoneData,
    GetPortfolioCurrentZoneDataReply
} from '../current-zone-indicator/model/cqrs.class';
import {CurrentZoneChangedMessage} from '../current-zone-indicator/popover/zone-levels-popover.component';
import {StrategiesSectionModel} from './model/StrategiesSectionModel';
import {GetAdjustmentSolutionsReply} from './model/GetAdjustmentSolutionsReply';
import {SettingsSectionModel} from './model/SettingsSectionModel';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {
    ApgPortfolioStateChangedDto,
    CashFlowStrategyRole, QuoteGeneratorQuoteDto,
    ServerTimeMessageDto,
    StrategyPriceDto,
    SweetPriceAvailableDto,
    TrackingNotificationSettingsUpdated,
    TrackingRequestErrorMessage,
    TrackingStartedMessage,
    TrackingStoppedMessage,
    TrackingTemplateUpdated
} from '../shell-communication/shell-dto-protocol';
import {MessageBusService} from '../message-bus.service';
import {debounce, debounceTime, filter, map, switchMap, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, merge, Observable, Subject, timer} from 'rxjs';
import {PriceboxSectionModel} from './model/PriceboxSectionModel';
import {ToastrService} from 'ngx-toastr';
import {OptionsChainService} from '../option-chains.service';
import {ShellTodayService} from '../shell-clock/shell-today.service';
import {AtmStrikeService} from '../common-services/atm-strike-service/atm-strike.service';
import {CashFlowStrategy} from '../adjustment-control-panel/cash-flow-strategy';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {DxScrollViewComponent} from 'devextreme-angular/ui/scroll-view';
import {DxPopupComponent} from 'devextreme-angular/ui/popup';
import {OrionWindowController} from '../copilot-shared/orion-window.controller';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {CashFlowStrategyTemplatesService} from './services/cashflow-strategy-templates.service';
import {SettingsStorageService} from '../settings-storage-service.service';
import {AccessControlService} from '../access-control-service.class';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {ICashFlowAdjustmentSettingsTemplate} from './model/ICashFlowAdjustmentSettingsTemplate';
import {BeforePositionDto} from './model/BeforePositionDto';
import {PositionsRestoredEventArgs, PositionsSectionModel} from './positions-section/model/PositionsSectionModel';
import {LocationService} from '../location.service';
import {SessionService} from '../authentication/session-service.service';
import {CopyOrdersToService} from './services/copy-orders-to.service';
import {ApplicationSettingsService, AppSettingsUpdatedMessageTopic} from '../app-settings/application-settings.service';
import {PositionsStateService} from './services/positions-before-state.service';
import {SavedPositionsService} from './services/saved-positions.service';
import {ServiceConfiguration} from './services/ServiceConfiguration';
import {AssignableAdjustmentTemplatesService} from './settings-section/assignable-adjustment-templates.service';
import {ApgPortfolio, findDefaultPortfolio} from './model/ApgPortfolio';
import {CashFlowPortfoliosService} from './services/cashflow-portfolios.service';
import {PositionsAfterStateService} from './services/positions-after-state.service';
import {AdjustmentSolutionPopupComponent} from './solution-popup/adjustment-solution-popup.component';
import {StrategyCodeIndex} from './model/StrategyCodeIndex';
import {QuoteIndex} from './model/QuoteIndex';
import {CashFlowAdjustment} from './model/CashFlowAdjustment';
import {ApgCopilotComponent} from './copilot/apg-copilot.component';
import {IApgCopilotFacade} from './copilot/apg-copilot-facade';
import {RollPreviewKey, RollSuggestionContent} from './copilot/apg-copilot-domain';
// CR-20260904-001 (Filtering rules, unit F3): the landmine map the ORION run filters on and the
// Price Box colours. ONE map instance per span, held by the host, so a card and a column cannot
// disagree about the same date (contract C1 G7).
import {LandmineDaysService} from './services/landmine-days.service';
import {LandmineDayMap} from './services/landmine-day.model';
import {LandmineSpan, landmineSpanOf, stabiliseSpan} from './copilot/apg-roll-window';
import {dayKey as apgDayKey} from './copilot/apg-copilot-questionnaire';
import {UserDto} from '../authentication/dtos/auth-result-dto.inteface';
import {ApgPortfolioRefreshDialogComponent} from './portfolio-refresh-dialog/apg-portfolio-refresh-dialog.component';
import {SweetPriceTrackingService} from './services/sweet-price-tracking.service';
import {SweetPriceSettings} from './positions-section/tracking-dialog/SweetPriceTrackingSettings';
import {SweetPricePopupConfig} from './positions-section/tracking-dialog/apg-tracking-dialog.component';
import {
    ApgTrackingSyncDialogComponent
} from './positions-section/tracking-sync-dialog/apg-tracking-sync-dialog.component';
import {GenericConfirmationDialogComponent} from '../generic-confirmation-dialog/generic-confirmation-dialog.component';
import {PriceboxPinnedRowsService} from './services/pricebox-pinned-rows.service';
import {ClipboardService} from "../clipboard.service";
import {PositionsData} from "./positions-section/model/PositionsData";
import {PriceboxMenuComponent} from "./pricebox-menu/pricebox-menu.component";
import {SolutionPositionDto} from "./model/SolutionPositionDto";
import {OffsetsManager, VisibleOffsetsSettingsService} from "./visible-offsets/visible-offsets-settings.service";
import {BeforePositionModel} from "./positions-section/model/BeforePositionModel";
import {SolutionOrderLegDto} from "./model/SolutionOrderLegDto";
import {SolutionOrderLegActions} from "./model/SolutionOrderLegActions";
import {DefaultSettingsMemento, DefaultsMemento} from "./positions-section/model/BucketBeforeStateDefaultsPopupModel";
import {DateTime} from "luxon";
import {makeOptionTicker} from "../options-common/options.model";
import {AfterStateDetalizationComponent} from "./after-state-detalization/after-state-detalization.component";
import {PriceboxRow} from "./model/PriceboxRow";
import {ZonesGridSettings, ZonesGridSettingsComponent} from "./zones-grid-settings/zones-grid-settings.component";
import {ZonesGridComponent} from "./zones-grid/zones-grid.component";
import {AtmStrikeChangedEvent} from "../common-services/atm-strike-service/atm-strike-changed-event";
import {ZonesGridMenuComponent} from "./zones-grid-menu/zones-grid-menu.component";
import {AfterStateDetalizationService} from "./after-state-detalization.service";
import {ApgHedgesTotalComponent} from "./hedges-total/apg-hedges-total.component";
import {ApgOriginalClientPositionsService} from "./services/apg-original-client-positions.service";
import * as Enumerable from "linq";
import {UserSettingsService} from "../user-settings.service";
import {HgSettingsSectionComponent} from "../hedging-grid/settings-section/hg-settings-section.component";
import {
    ApgSectionVisibility,
    ApgSectionVisibilityService,
    makeDefaultSectionVisibility
} from "./services/apg-section-visibility.service";
import {ApgSectionVisibilityMenuComponent} from "./section-visibility-menu/apg-section-visibility-menu.component";
import {AdjustmentGridSettingsSectionComponent} from './settings-section/adjustment-grid-settings-section.component';
import {OverrideAtmDialogComponent} from "../override-atm-dialog/override-atm-dialog.component";
import {HedgePositionsService} from "../hedging-grid/positions-section/hedge-positions/hedge-positions.service";
import {FavoriteAdjustment} from "./pricebox-menu/FavoriteAdjustment";
import {ApgPricingStepsPopupComponent, ApgPricingStepsConfig} from "./apg-pricing-steps-popup/apg-pricing-steps-popup.component";
import {PriceboxColumn} from "./model/PriceboxColumn";
// CR-20260904-001 (Presentation): Price Box landmine highlighting.
import {
    buildLandmineMarks,
    LandmineHighlightSwitches,
    parsePriceboxExpirationLabel,
    priceboxDayKey
} from "./landmine-highlight/pricebox-landmine-marks";
import {
    buildLandmineTooltip,
    LandmineTooltipContent
} from "./landmine-highlight/pricebox-landmine-tooltip";
import {
    APG_SELECT_PORTFOLIO_REQUEST_TOPIC,
    OpenSubjectRequest,
    SELECT_REQUEST_TTL_MS
} from "../action-board/action-board.model";

export type ApgGridColumn = 'positions' | 'settings' | 'strategies' | 'pricebox' | 'zones-grid';

type DesiredMementoState = { expiration: string, strike: number, qty: number };

/**
 * Request-id tags of the `GetAdjustmentSolutions` replies whose rows were CHOSEN BY THE BACKEND (or
 * by a rule), not by the trader's saved favourites — so the favourites filter must not hide them
 * (owner ruling 2026-09-08, see `PriceboxSectionModel.rowsSelectedByBackend`).
 *
 *  - 'orion-'      : an ORION delivery, put through `populatePriceBoxFromOrion` (CR-20260910-001).
 *  - 'zone-drift-' : the zone-drift one-shot's #2B rows (CR-20260927-003).
 *
 * Fix 2026-09-27: `populatePriceBoxFromOrion` has tagged its reply 'orion-' since CR-20260910-001,
 * but the check still looked for the pre-migration 'copilot-' tag that no producer emits any more —
 * so an ORION delivery was silently subject to the favourites filter. One list, one check.
 */
const BACKEND_SELECTED_REQUEST_ID_PREFIXES = ['orion-', 'zone-drift-'];

function isBackendSelectedRequestId(requestId: string): boolean {
    return !isVoid(requestId)
        && BACKEND_SELECTED_REQUEST_ID_PREFIXES.some(prefix => requestId.startsWith(prefix));
}

@Component({
    selector: 'ets-adjustment-pricing-grid',
    templateUrl: 'adjustment-pricing-grid.component.html',
    styleUrls: [
        './adjustment-pricing-grid.component.scss',
        './adjustment-grid-common-style.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        PositionsStateService,
        SavedPositionsService,
        CopyOrdersToService,
        CashFlowStrategyTemplatesService,
        CashFlowPortfoliosService,
        PositionsAfterStateService,
        PriceboxPinnedRowsService
    ]
})
export class AdjustmentPricingGridComponent implements OnInit, AfterViewInit, IApgCopilotFacade {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
        private readonly _atmStrikeService: AtmStrikeService,
        private readonly _optionChainsService: OptionsChainService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _positionsStateService: PositionsStateService,
        private readonly _cashFlowStrategyTemplatesService: CashFlowStrategyTemplatesService,
        private readonly _accessControlService: AccessControlService,
        tiService: TradingInstrumentsService,
        locationService: LocationService,
        private readonly _sessionService: SessionService,
        private readonly _copyOrdersToService: CopyOrdersToService,
        private readonly _applicationSettingsService: ApplicationSettingsService,
        private readonly _savedPositionsService: SavedPositionsService,
        private readonly _assignTemplatesService: AssignableAdjustmentTemplatesService,
        private readonly _portfoliosService: CashFlowPortfoliosService,
        private readonly _positionsAfterStateService: PositionsAfterStateService,
        private readonly _sweetPriceTrackingService: SweetPriceTrackingService,
        private readonly _priceboxPinnedRowsService: PriceboxPinnedRowsService,
        private readonly _clipboardService: ClipboardService,
        private readonly _visibleOffsetsService: VisibleOffsetsSettingsService,
        private readonly _apgOriginalClientPositionsService: ApgOriginalClientPositionsService,
        private readonly _hedgesService: HedgePositionsService,
        // CR-20260904-001 (Filtering rules): the Events Calendar's SINGLE fetch point.
        private readonly _landmineDaysService: LandmineDaysService,
        private readonly _sectionVisibilityService: ApgSectionVisibilityService,
        private readonly _shellToday: ShellTodayService
    ) {
        this.strategiesSection = new StrategiesSectionModel();

        this.positionsSection = new PositionsSectionModel(
            _changeDetector,
            this._unsubscriber,
            _positionsStateService,
            () => this.canApplyChanges(),
            _accessControlService,
            _atmStrikeService,
            _toastr,
            locationService,
            _sessionService,
            _applicationSettingsService,
            _savedPositionsService,
            _messageBus,
            'scopeID',
            tiService,
            _positionsAfterStateService,
            this._userSettingsService
        );

        this.positionsSection.setEventHandler(this as any);
        this.positionsSection.defaultsUpdated$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(async x => {
                try {
                    await this.generateAdjustmentOrders(x)
                } catch (e) {
                    console.error(e);
                }
            })

        this.settingsSection = new SettingsSectionModel(
            _changeDetector,
            this._unsubscriber,
            _optionChainsService,
            _cashFlowStrategyTemplatesService,
            _toastr,
            this._sessionService,
            tiService,
            this._assignTemplatesService,
            _accessControlService,
            _applicationSettingsService
        );

        this.settingsSection.setContextDataProvider(this as any);

        this.priceboxSection = new PriceboxSectionModel(
            _changeDetector,
            this._unsubscriber,
            this._priceboxPinnedRowsService,
            this as any,
            this._visibleOffsetsService
        );

        AdjustmentPricingGridComponent.instanceId += 1;

        this.positionSectionUniqueId = 'ets-apg-positions' + AdjustmentPricingGridComponent.instanceId;
        this.portfolioSectionUniqueId = 'ets-apg-portfolio' + AdjustmentPricingGridComponent.instanceId;

        const availableStrats = getAvailableCashFlowStrategies(this._accessControlService);

        this.portfolioPopup = {
            isVisible: false,
            strategies: availableStrats
        };

        this._tradingInstrumentsService = tiService;

        this._strategyCodeIndex = new StrategyCodeIndex(_lastQuoteCache);
        this._quoteIndex = new QuoteIndex(_lastQuoteCache);

        this._afterStateDetalizationService.ready$.subscribe(() => this.onAfterStateDetalizationReady())
    }

    private readonly _afterStateDetalizationService = new AfterStateDetalizationService();

    // CR-20260928-001: resolves the pasted underlying to an instrument for the paste offer.
    private readonly _tradingInstrumentsService: TradingInstrumentsService;

    private _priceboxSectionHeaderVolatility;

    private _zonesGridSectionHeaderVolatility;

    private _isWaitingAtmChangeEvent: boolean = false;

    //
    private static instanceId = 0;

    //
    private readonly _unsubscriber = new Subject<void>();

    //
    private readonly _strategyCodeIndex: StrategyCodeIndex;

    //
    private readonly _quoteIndex: QuoteIndex;

    //
    private _longRunningOperationClients = 0;

    //
    readonly positionSectionUniqueId: string;

    //
    readonly portfolioSectionUniqueId: string;

    get templatesService(): CashFlowStrategyTemplatesService {
        return this._cashFlowStrategyTemplatesService;
    }

    //
    sectionCollapsed$ = new EventEmitter<{ section: ApgGridColumn, state: boolean }>();

    @ViewChild(AdjustmentSolutionPopupComponent, {static: true})
    solutionPopupCmp: AdjustmentSolutionPopupComponent;

    //
    @ViewChild(DxScrollViewComponent, {static: false})
    scrollView: DxScrollViewComponent;

    //
    @ViewChild(ApgPortfolioRefreshDialogComponent)
    portfolioRefreshDialog: ApgPortfolioRefreshDialogComponent;

    //
    @ViewChild(ApgTrackingSyncDialogComponent)
    syncTrackingDialog: ApgTrackingSyncDialogComponent;

    @ViewChild(PriceboxMenuComponent)
    priceBoxMenu: PriceboxMenuComponent;

    @ViewChild(ZonesGridMenuComponent)
    zonesGridMenu: ZonesGridMenuComponent;

    @ViewChild(ApgSectionVisibilityMenuComponent)
    sectionVisibilityMenu: ApgSectionVisibilityMenuComponent;

    @ViewChild(AfterStateDetalizationComponent)
    afterStateDetalizationDialog: AfterStateDetalizationComponent;

    @ViewChild(ZonesGridSettingsComponent)
    zonesGridSettingsDialog: ZonesGridSettingsComponent;

    @ViewChild(ZonesGridComponent)
    zonesGridSection: ZonesGridComponent;

    @ViewChild(ApgHedgesTotalComponent)
    hedgesTotalCmp: ApgHedgesTotalComponent;

    @ViewChild(OverrideAtmDialogComponent)
    overrideAtmDialog: OverrideAtmDialogComponent;

    @ViewChild(ApgPricingStepsPopupComponent)
    apgPricingStepsPopup: ApgPricingStepsPopupComponent;

    showHedges = true;

    //
    @Input()
    orientation: 'left' | 'right';

    /** CR-20260924-001 F4b: the workspace panel id this grid lives in (from the panel wrapper); a
     * deep link stamped with another panel's id is not ours. */
    @Input()
    panelId: string;

    @Input() layoutTabId: string;

    // CR-20260927-003: the wrapper panel's active state (it hides this grid with a class while
    // another tab is on screen). A dx-popup opened while the grid is hidden never shows and never
    // learns when the grid appears, so a waiting zone-drift one-shot is fired HERE, on activation.
    private _panelIsActive = false;

    @Input()
    set panelIsActive(v: boolean) {
        const was = this._panelIsActive;
        this._panelIsActive = !!v;
        if (!was && this._panelIsActive) {
            this.zoneDriftConditionMet('active', 'panel activated');
        }
    }

    get isZonesGridAvailable(): boolean {
        return this._accessControlService
            .isSecureElementAvailable('9c43574d-4f20-423f-a68a-bbb054207aa4');
    }

    get isPricingStepsAvailable(): boolean {
        return this._accessControlService
            .isSecureElementAvailable('d8f4e2a1-3b7c-4e9d-a5f2-8c6b1d0e9f3a');
    }

    // ===== APG CoPilot (advisory roll-positions assistant) ==============================
    // Entitlement-gated + purely additive. The host implements IApgCopilotFacade; the CoPilot
    // NEVER mutates positions/orders — its only host effects are the advisory Preview highlight
    // and opening the EXISTING review dialog.

    @ViewChild(ApgCopilotComponent)
    apgCopilot: ApgCopilotComponent;

    // CR-20260904-001, Zones Z7. The selected portfolio's current zone, kept for the synchronous
    // readCurrentZone() the ORION Zone view reads. null = N/A.
    private _currentZone: number | null = null;

    copilotPopupVisible = false;
    copilotHasActivePreview = false;

    // Grid-drift cue pushed up by the CoPilot child's (staleChange). Mirrored onto the collapsed FAB —
    // the only surface the user sees while the window is shut. The CoPilot owns the comparison; the host
    // owns the FAB.
    copilotIsStale = false;

    // ORION <-> APG exclusive mode (O88-O93). true from Ask ORION until Reset ORION / portfolio
    // switch / template load. Drives the glass over the positions + settings columns and kills
    // canApplyChanges(). Host-owned so it survives the ORION window closing. Session only.
    orionHoldsGrid = false;

    // CR-20260927-003: the zone-drift one-shot in flight (the zone the Action Board card named).
    // Read by getAdjustmentPricingSettingsObject (forces the #2B filter) and loadData (tags the
    // request). Set and cleared ONLY by runZoneDriftOneShot; a manual Apply never sees it.
    private _zoneDriftOneShot: { zone: number } | null = null;

    @ViewChild(AdjustmentGridSettingsSectionComponent, {static: false})
    private _settingsSectionCmp: AdjustmentGridSettingsSectionComponent;

    // The settings glass swallows the wheel; forward it so the held settings stay READABLE below
    // the fold (Alan 2026-09-08: "what if I want to check what other settings are below?").
    onSettingsGlassWheel(event: WheelEvent): void {
        if (!this._settingsSectionCmp) {
            return;
        }
        event.preventDefault();
        this._settingsSectionCmp.scrollBy(event.deltaY);
    }

    // CoPilot FAB position (A7/WI-11). null = the CSS default (panel bottom-right). A drag writes {left, top}
    // px here via (etsDraggablePositionChange); it lives on the component so it survives the FAB's *ngIf
    // teardown when the popup opens. Session only — a reload returns to the default corner.
    copilotFabPosition: { left: number; top: number } | null = null;

    private _copilotPreviewKey: RollPreviewKey | null = null;

    // ---- dynamic popup height (owner 2026-07-07 "just as in HG Copilot") ----
    // Duplicated from HedgingGridComponent: the popup is pinned to the top and auto-fits the CoPilot
    // child's reported content height, growing DOWNWARD until the bottom gap equals the top offset, then
    // the child's dx-scroll-view takes over. WINDOW-relative cap; drag/resize never change it.
    private static readonly COPILOT_TOP_OFFSET_PX = 24;   // symmetric top == max bottom gap
    private static readonly COPILOT_CHROME_PX = 56;       // title bar + popup padding
    private static readonly COPILOT_MIN_HEIGHT_PX = 240;
    copilotPopupPosition: any = {
        my: 'center top', at: 'center top', of: window,
        offset: { x: 0, y: AdjustmentPricingGridComponent.COPILOT_TOP_OFFSET_PX }
    };
    copilotPopupHeight = 420;                             // seed; corrected to content on the first measure
    private _copilotContentHeight = 0;

    // APG CoPilot's OWN secure-element GUID - distinct from the Hedge CoPilot's (3ffd6a93-...);
    // the two are separate entitlements by design. Registered in the backend catalog
    // (ETS.CompanyServicesService/Data/UIElement.cs -> GetEtsWebTraderUIElements, CR-20260707-001);
    // this GUID MUST match that catalog entry. Server-driven gate (ships dark): the CoPilot is visible
    // only for users granted this secure-element. NOTE: the FAB stays hidden until the CompanyServices
    // redeploy carrying that catalog entry is LIVE and the element is granted (BusinessConsole service
    // pack -> ClientConsole role/user).
    isApgCoPilotAvailable(): boolean {
        return this._accessControlService
            .isSecureElementAvailable('0d9f3222-88ec-453b-af78-c8636b18b192');
    }

    @DetectMethodChanges()
    openApgCopilot(): void {
        this._applyCopilotPopupHeight();   // clamp the seed to the current window before the first measure
        this.copilotPopupVisible = true;
        if (this.apgCopilot) {
            this.apgCopilot.open();
        }
    }

    @DetectMethodChanges()
    onCopilotPreviewActiveChange(active: boolean): void {
        this.copilotHasActivePreview = active;
    }

    // The CoPilot child reports the live grid has drifted from the state it captured at run time.
    // Mirrored onto the collapsed launcher — the only cue left once the window is shut. @DetectMethodChanges
    // because this host is OnPush and the event arrives from a child @Output while the popup is closed.
    @DetectMethodChanges()
    onCopilotStaleChange(stale: boolean): void {
        this.copilotIsStale = stale;
    }

    @DetectMethodChanges()
    onCopilotCloseRequested(): void {
        this.copilotPopupVisible = false;
    }

    // dx-popup fully shown: force the CoPilot child to re-sync its OnPush view. On reopen the child's
    // state survives (deferRendering=false keeps it mounted) but the view can render stale (the
    // collapsed breadcrumbs disappear) because the popup content is not in the CD tree during the
    // host's post-open detectChanges pass.
    onCopilotShown(): void {
        if (this.apgCopilot) {
            this.apgCopilot.refreshView();
        }
        // Re-assert the window zoom + containment: the overlay element can be recreated when the
        // popup's *ngIf re-mounts it, and the edge strip must be (re)parented onto the overlay.
        this._orionWindow.apply();
    }

    // ---- ORION window chrome (shared zoom hatch + viewport containment) --------------------
    // Owner 2026-08-31: "this zoom hatch needs to be implemented for every copilot." Mechanism
    // (scale-transform zoom, ear drag, MutationObserver re-assert, viewport clamp) lives in the
    // shared OrionWindowController; these thin wrappers keep the template bindings +
    // @DetectMethodChanges (this host is OnPush).
    private readonly _orionWindow = new OrionWindowController(() => this.copilotPopupRef);

    @ViewChild('copilotPopupRef')
    copilotPopupRef: DxPopupComponent;

    get copilotZoomLevel(): number {
        return this._orionWindow.zoomLevel;
    }

    get copilotZoomPct(): number {
        return this._orionWindow.zoomPct;
    }

    @DetectMethodChanges()
    copilotZoomIn(): void {
        this._orionWindow.zoomIn();
    }

    @DetectMethodChanges()
    copilotZoomOut(): void {
        this._orionWindow.zoomOut();
    }

    @DetectMethodChanges()
    onCopilotZoomStripDblClick(ev: MouseEvent): void {
        this._orionWindow.onStripDblClick(ev);
    }

    onCopilotZoomStripPointerDown(ev: PointerEvent): void {
        this._orionWindow.onStripPointerDown(ev);
    }

    // The CoPilot child reports its natural content height; clamp it into the popup (top pinned, grows
    // downward until the symmetric bottom gap, then the child's dx-scroll-view scrolls). Duplicated from HG.
    @DetectMethodChanges()
    onCopilotContentHeight(contentHeight: number): void {
        this._copilotContentHeight = contentHeight;
        this._applyCopilotPopupHeight();
    }

    private _applyCopilotPopupHeight(): void {
        const maxH = this._copilotMaxHeight();
        const desired = this._copilotContentHeight + AdjustmentPricingGridComponent.COPILOT_CHROME_PX;
        const min = AdjustmentPricingGridComponent.COPILOT_MIN_HEIGHT_PX;
        this.copilotPopupHeight = Math.max(min, Math.min(desired, maxH));
    }

    // maxHeight = the full window minus an equal gap top + bottom. Floored at the min so a very short
    // window never collapses the popup (the symmetric gap is then sacrificed — accepted).
    private _copilotMaxHeight(): number {
        const symmetric = window.innerHeight - 2 * AdjustmentPricingGridComponent.COPILOT_TOP_OFFSET_PX;
        return Math.max(AdjustmentPricingGridComponent.COPILOT_MIN_HEIGHT_PX, symmetric);
    }

    // Window resize changes the cap; re-clamp the live popup so it never spills past the symmetric margins.
    @HostListener('window:resize')
    onCopilotWindowResize(): void {
        this._onCopilotWindowResizeImpl();
    }

    @DetectMethodChanges()
    private _onCopilotWindowResizeImpl(): void {
        if (this.copilotPopupVisible) {
            this._applyCopilotPopupHeight();
            // A resize (incl. browser-zoom changes) can shrink the viewport past the window's
            // position — re-clamp so the window stays fully inside it (CR HicgA7n5).
            this._orionWindow.clampNow();
        }
    }

    // True for the ONE Price Box cell the active Preview points at (adjustmentType[+2] x expiration).
    isCopilotPreviewCell(row: PriceboxRow, column: PriceboxColumn): boolean {
        const k = this._copilotPreviewKey;
        if (!k || !row || !column) {
            return false;
        }
        if (row.adjustmentType !== k.adjustmentType) {
            return false;
        }
        const rowType2 = row.adjustmentType2 || null;
        const keyType2 = k.adjustmentType2 || null;
        if (rowType2 !== keyType2) {
            return false;
        }
        return column.expirationDatePretty === k.expirationDatePretty;
    }

    // ---- IApgCopilotFacade -------------------------------------------------------------

    isEntitled(): boolean {
        return this.isApgCoPilotAvailable();
    }

    readSelectedPortfolioId(): string | null {
        return this._selectedPortfolio ? this._selectedPortfolio.id : null;
    }

    // The COMPOSITE portfolio key, `userId@id` — the only form the backend can resolve a zone by.
    // The same `makePortfolioKey` the zone read at `readCurrentZone`, the message-bus filter and the
    // old roll-scoped pricing call all use; this restores it to the migrated run, which regressed to
    // the bare id and made every portfolio look zone-less to the backend.
    readSelectedPortfolioKey(): string | null {
        const key = makePortfolioKey(this._selectedPortfolio);
        return key ? key : null;
    }

    /**
     * The LIVE, quote-driven price getter of ONE Price Box cell (IApgCopilotFacade).
     *
     * ⚠ WHY A GETTER AND NOT A NUMBER. The ORION card's money must tick off the SAME accessor the
     * cell binds. Handing back a snapshot is exactly the defect the reason line was rebuilt to kill:
     * a +$32.43 header against a $32.38 sentence, both "correct" at different instants.
     *
     * Null when no such cell is loaded — the card then keeps the frozen figure the delivery carried,
     * rather than showing nothing.
     *
     * ⚠ WHAT WAS HERE BEFORE 2026-09-10 (CR-20260910-001 milestone 1): `readCandidates()`,
     * `countPendingPriceCells()` and `readFilteredOutDates()`. They existed to feed a BROWSER skill
     * the loaded Price Box cells and then to explain, on a coverage note, which days that reading
     * had missed. The run happens on the backend now and the Price Box is populated FROM its
     * delivery, so there is nothing to read cells INTO and nothing to explain — ruling R-C moved the
     * dropped-day record to the backend log. Do not reintroduce a cell reader here: "what the grid
     * managed to load" stopped being an input to the run, and making it one again would put the
     * settle race back.
     */
    readLivePriceAccessor(key: RollPreviewKey): (() => number) | null {
        if (!key) {
            return null;
        }
        const rows = this.priceboxSection ? this.priceboxSection.rows : [];
        const keyType2 = key.adjustmentType2 || null;

        for (let rx = 0; rx < rows.length; rx++) {
            const row = rows[rx];
            if (row.adjustmentType !== key.adjustmentType) {
                continue;
            }
            if ((row.adjustmentType2 || null) !== keyType2) {
                continue;
            }
            const columns = row.columns || [];
            for (let cx = 0; cx < columns.length; cx++) {
                const col = columns[cx];
                if (col.expirationDatePretty === key.expirationDatePretty) {
                    // `col` is captured per iteration, so each closure holds its own cell.
                    return () => col.price;
                }
            }
        }
        return null;
    }

    // ---- Preview / Review (advisory only: a highlight and the existing dialog) ------------------

    @DetectMethodChanges()
    setPreviewCell(key: RollPreviewKey): void {
        this._copilotPreviewKey = key;
    }

    @DetectMethodChanges()
    clearPreviewCell(): void {
        this._copilotPreviewKey = null;
    }

    openReview(adjustments: CashFlowAdjustment[]): void {
        this.showPopup(adjustments, 'PriceBox');
    }

    // The roll-date candidate universe = the option chain's listed expirations ("yyyy-MM-dd"),
    // filtered to visible (daysToExpiration >= 0). A date not listed here is not rollable (weekends
    // never appear). The questionnaire's range/exclusions are resolved against THIS list.
    async readAvailableExpirations(): Promise<string[]> {
        const underlying = this.settingsSection.underlying;
        if (isVoid(underlying)) {
            return [];
        }
        const chain = await this._optionChainsService.getChain(underlying);
        if (isVoid(chain) || isVoid(chain.expirations)) {
            return [];
        }
        return chain.expirations
            .filter(x => x.daysToExpiration >= 0)
            .map(x => x.optionExpirationDate);
    }

    // The ORION "today" (IApgCopilotFacade.readTodayKey). QG shell: the cursor day of the current
    // underlying, by the synchronous request/reply-by-mutation pattern every QG consumer uses
    // (hedge-matrix getCursorDate, CollarsQgCursorService). Live shell: the day part of the last
    // ServerTimeMessageDto, converted exactly as the status-bar clock converts it (an emulated
    // "fake" time is a wall-clock value in the shell's zone; a real time is shown in the default
    // zone), so ORION's "today" is the day the trader reads in the status bar.
    readTodayKey(): string | null {
        const login = this._sessionService.loginResult;
        if (login && login.isQuoteGeneratorShell) {
            const underlying = this.settingsSection.underlying;
            if (isVoid(underlying)) {
                return null;
            }
            const msg: { ticker: string; cursorDate: string } = { ticker: underlying, cursorDate: undefined };
            this._messageBus.publish({ topic: 'Qg.CursorDateRequest', payload: msg });
            return isVoid(msg.cursorDate) ? null : msg.cursorDate.split('T')[0];
        }
        const st = this._lastServerTime;
        if (!st || !st.time) {
            return null;
        }
        let dt: DateTime;
        if (st.isFakeTime) {
            dt = DateTime.fromJSDate(st.time, { zone: 'UTC' }).setZone(st.timeZone || 'UTC', { keepLocalTime: true });
        } else {
            dt = DateTime.fromJSDate(st.time).setZone(this._applicationSettingsService.defaultTimezone || 'UTC');
        }
        return dt.isValid ? dt.toFormat('yyyy-LL-dd') : null;
    }
    private _lastServerTime: ServerTimeMessageDto | null = null;

    // The ORION Date-range Start anchor (IApgCopilotFacade.readRollAnchorKey, owner 2026-09-08).
    // The EARLIER ShortOption expiration across the sides (owner: "earlier"), or "today" when that
    // expiration is already behind the authoritative clock (owner: "fallback today"). Null = no
    // ShortOption leg, or one whose expiration is not selected: the panel then neither seeds nor
    // blocks. Same short-leg lookup as orionZoneAtm, so the two ORION rules read the same legs.
    readRollAnchorKey(): string | null {
        const shortKeys = (this.positionsSection.positionsData || [])
            .map(side => side.positions.find(p => p.role === 'ShortOption'))
            .filter(p => !isVoid(p) && !isVoid(p.selectedExpiration) && !isVoid(p.selectedExpiration.optionExpirationDate))
            .map(p => p.selectedExpiration.optionExpirationDate.split('T')[0])
            .filter(key => /^\d{4}-\d{2}-\d{2}$/.test(key))
            .sort();
        if (shortKeys.length === 0) {
            return null;
        }
        const earliest = shortKeys[0];
        const today = this.readTodayKey();
        // Keys are "yyyy-MM-dd", so a string compare is a day compare.
        return today && today > earliest ? today : earliest;
    }

    // Build the expanded template settings an ORION run is priced on (IApgCopilotFacade).
    //
    // Mirrors what an Apply builds, with two deliberate differences:
    //   - the sequential fan-out is suppressed (the three look-forward counts are zero, so the
    //     backend loop runs zero iterations and nothing is added beyond the window);
    //   - `customDates` is LEFT EMPTY. The backend resolves the window from the questionnaire it
    //     was sent (contract A1 §4.1). It has to: Maven builds this same array on the backend from
    //     a template id and has no browser to ask, so the window rule lives in exactly one place.
    //
    // Rejects when the grid's settings are not valid — that is a thing the trader must fix before a
    // run means anything, and the toasts say which.
    async buildOrionPricingSettings(): Promise<AdjustmentPricingSettingsDto[]> {
        const built = await this.getAdjustmentPricingSettingsObject('Price Box');

        if (!isVoid(built.errors)) {
            built.errors.forEach(x => this._toastr.error(x));
            throw new Error('ORION: settings are not valid');
        }

        built.settings.forEach(s => {
            s.customDates = [];
            s.expirationsToLookForward = 0;
            s.expirationsToLookForwardAbove = 0;
            s.expirationsToLookForwardDown = 0;
        });

        return built.settings;
    }

    /**
     * POPULATE THE PRICE BOX FROM THE ORION DELIVERY (IApgCopilotFacade; ruled 2026-09-10).
     *
     * ⚠ WHAT THIS REPLACES, AND WHY IT IS SMALLER THAN IT LOOKS. `requestRollAdjustments()` used to
     * fire a roll-scoped `GetAdjustmentSolutions`, tag it with a 'copilot-' request id, and then run
     * a TWO-LEG settlement — a give-up timer for the reply, then an 80ms poll waiting for the cells
     * to price — so it could hand freshly-priced candidates back to a browser skill. All of it is
     * gone. The delivery already carries every window adjustment, priced on the backend.
     *
     * The seam is small because the shapes already match: the delivery's adjustments are
     * `CashFlowAdjustment[]`, which is exactly what `GetAdjustmentSolutionsReply.adjustments`
     * carries. So they are put into a synthesized reply and driven through the SAME load path an
     * Apply reply takes — `onDataLoadedInternally` — and the columns then price live from their own
     * strategy-code subscriptions, as they always have.
     *
     * ⚠ WHAT ARRIVES HERE IS THE UNION, NOT THE CARDS (leader rulings L37 / L50). The caller passes
     * the cards' adjustments PLUS the window cells that produced no card. Flattening the cards here
     * instead would empty the column of every Monday and every report day — the columns that are on
     * screen today with their credits in them — and milestone 1's gate is a PARITY smoke, so it
     * would fail there. Nothing in this method may filter, group or de-duplicate by date: a no-zone
     * portfolio legitimately prices two cells on one date.
     *
     * There is NO settle poll any more, and that is the point: nothing downstream waits for a price,
     * so a one-tick gap in the quote stream can no longer resolve a run on a partial set.
     */
    async populatePriceBoxFromOrion(adjustments: CashFlowAdjustment[]): Promise<void> {
        const window = adjustments || [];

        // Clear the prior pricing artifacts (the quote/strategy indexes ACCUMULATE via addData) —
        // the same clean slate an Apply gets, WITHOUT resetStateFull()'s positions/settings wipe, so
        // the trader's inputs stay intact.
        this.resetPricingData();

        if (window.length === 0) {
            return;   // nothing was calculated at all; the panel says so in one sentence
        }

        const reply: GetAdjustmentSolutionsReply = {
            error: false,
            // Tagged so the log and any correlation reader can see where these columns came from.
            // It is NOT in _pendingRequestId: nothing is in flight, the answer is already here.
            // The tag MUST be one of BACKEND_SELECTED_REQUEST_ID_PREFIXES: it is what lifts the
            // favourites filter off the delivered rows in onDataLoadedInternally.
            requestId: BACKEND_SELECTED_REQUEST_ID_PREFIXES[0] + getShortUUID(),
            adjustments: window
        };

        this.isDataLoading = true;
        try {
            await this.onDataLoadedInternally(reply);
        } finally {
            this.isDataLoading = false;
        }

        console.log(
            '[orion] Price Box fed from the delivery - adjustments ' + window.length +
            ' (cards + window cells, unioned by the client, de-duplicated by nothing). ' +
            'No pricing call was made; the columns price from their own strategy-code subscriptions.');
    }

    // Owner ruling 2026-09-08 (second of the day, Ask ORION): NO Override-ATM pop-up. ORION has two
    // moves only, and the zone already decides which one the backend prices (O74): shorts off the
    // zone => move to the zone (#2B), which needs the zone as the custom ATM; shorts on the zone =>
    // roll sideways (#3), which needs the market ATM. The same fact fixes the ATM, so asking the
    // trader was a redundant step. Decided here at click time and PERSISTED into globalSettings so
    // it drives the calc, exactly as the pop-up's answer used to.
    // No zone (backend prices both, O76) => market ATM, silently. No short leg found => market ATM.
    // This REPLACES the earlier 2026-09-08 ruling that only prefilled the box with the zone.
    applyOrionAtm(): number {
        const zoneAtm = this.orionZoneAtm();
        const atm = isValidNumber(zoneAtm, true) ? zoneAtm : NaN;
        this.settingsSection.globalSettings.overrideAtm = atm;
        return atm;
    }

    // O88: Ask ORION takes the grid. The glass + the Apply gate read orionHoldsGrid. A control that
    // already had focus inside a held column is blurred so keys cannot reach it under the glass.
    @DetectMethodChanges()
    takeOrionHold(): void {
        this.orionHoldsGrid = true;
        const active = document.activeElement as HTMLElement | null;
        if (active && typeof active.blur === 'function'
            && active.closest('.positions-column, .settings-column')) {
            active.blur();
        }
    }

    // O91: the release is the ONLY way back. It also removes the residue ORION left in the shared
    // settings - the Override ATM it chose and the live-strikes ATM mode - so the next manual Apply
    // starts from an empty box ("the party starts again"). Direct assignment like applyOrionAtm and
    // the portfolio-switch path: no parameterChanged$ emission, no stale flash. Idempotent.
    @DetectMethodChanges()
    releaseOrionHold(): void {
        this.orionHoldsGrid = false;
        this.settingsSection.globalSettings.overrideAtm = null;
        this.positionsSection.overrideAtmLiveMode = null;
    }

    // The current zone when EITHER ShortOption strike differs from it (= move to the zone), else
    // undefined (= market ATM). No zone / no short leg => undefined.
    private orionZoneAtm(): number | undefined {
        const zone = this.readCurrentZone();
        if (!isValidNumber(zone, true)) {
            return undefined;
        }
        const shortStrikes = (this.positionsSection.positionsData || [])
            .map(side => side.positions.find(p => p.role === 'ShortOption')?.strike)
            .filter(strike => isValidNumber(strike, true));
        if (shortStrikes.length === 0) {
            return undefined;
        }
        return shortStrikes.some(strike => strike !== zone) ? zone : undefined;
    }

    // CR-20260904-001, Zones Z7 (contract C4). The selected portfolio's current zone, or null for
    // N/A. Synchronous by contract while GetPortfolioCurrentZoneData is not, so the value is
    // cached here and refreshed from onPortfolioSelected.
    //
    // Gated on the selection for the same reason readSelectedPortfolioId is: when the selection is
    // cleared, onPortfolioSelected returns early without refreshing, and a cached number would
    // then be the PREVIOUS portfolio's zone. No selection, no zone.
    readCurrentZone(): number | null {
        return this._selectedPortfolio ? this._currentZone : null;
    }

    // ---- Stale-grid drift signals (CoPilot reset button + FAB go yellow after a run) --------------
    // The CoPilot reuses the grid's EXISTING change detectors; the host just surfaces them + a value
    // fingerprint. The CoPilot captures the fingerprint at Run and re-compares on every edit (reversible).

    // Positions changed (up/down direction, strike, qty, ...) — the positions section's own event.
    observePositionsChanged(): Observable<void> {
        return this.positionsSection.positionsChanged$.pipe(map(() => void 0));
    }

    // Settings EDITS that count as drift (global + per-strategy + expirations). selectedTemplate is
    // EXCLUDED — a template load/reload is a context change routed to observeTemplateReloaded (hard reset),
    // not a drift edit; excluding it here also avoids a transient hot flash on load.
    observeSettingsChanged(): Observable<void> {
        const s = this.settingsSection;
        return merge(
            s.globalSettings.parameterChanged$.pipe(filter(x => x.property !== 'selectedTemplate')),
            s.strategyCalls.parameterChanged$,
            s.strategyPuts.parameterChanged$,
            s.strategyHedgePortfolio.parameterChanged$,
            s.strategyReversedHedgePortfolio.parameterChanged$,
            s.expirationsSettings.parameterChanged$
        ).pipe(map(() => void 0));
    }

    // Template load/reload -> the CoPilot HARD-RESETS. selectedTemplate is the load signal.
    observeTemplateReloaded(): Observable<void> {
        return this.settingsSection.globalSettings.parameterChanged$.pipe(
            filter(x => x.property === 'selectedTemplate'),
            map(() => void 0)
        );
    }

    // A stable VALUE fingerprint of the grid the CoPilot prices against: settings template (incl. the
    // Override-ATM the run persists via globalSettings.getSettings) + positions via the same asDto() used
    // for save/clipboard, so it is config-only (no live prices). Equal config -> equal string (reversible).
    readGridStateFingerprint(): string {
        try {
            const settings = this.settingsSection.getSettings();
            const positions = this.positionsSection.positionsData.map(p => p.positions.map(y => y.asDto()));
            return JSON.stringify({settings, positions});
        } catch {
            return '';   // settings/positions mid-construction (e.g. no template yet) -> empty, compares equal
        }
    }

    // ---- Landmine days (CR-20260904-001, Filtering rules unit F3) -----------------------------
    // WHY THE HOST OWNS THIS. Two surfaces read the same verdicts - the ORION run (which days may be
    // suggested) and the Price Box colouring (which columns are marked). If each fetched its own,
    // a card could say "clear" about a column painted red. So the host holds ONE map per span and
    // both read it (contract C1 G7).

    private _landmineDays: LandmineDayMap | null = null;

    // The span already asked about. HELD, so the number of distinct spans is bounded by the number
    // of WIDENINGS rather than by the number of column builds. `LandmineDaysService` is provided in
    // AdjustmentPricingGridModule, so this field has exactly the same lifetime as the cache it
    // protects. It is always month-aligned, because every span reaching here is quantized first.
    private _landmineSpan: LandmineSpan | null = null;

    // BehaviorSubject, NOT a plain Subject and NOT a ReplaySubject (leader's ruling, 2026-09-04).
    // Cause -> effect: Presentation subscribes in its own region and may subscribe AFTER the column
    // build has already published. A plain Subject would then deliver nothing and leave the Price Box
    // uncoloured with no error at all - a silent miss, not a visible failure. A ReplaySubject(1) has
    // the same hole before the first publish, because it has nothing to replay. A BehaviorSubject
    // always emits on subscribe.
    //
    // `null` IS A REAL EMISSION and it means "no calendar yet" - never an error, and never
    // "everything is clear". Nothing else is ever published as null.
    private readonly _landmineDaysChanged = new BehaviorSubject<LandmineDayMap | null>(null);

    // The member Presentation binds to. Read-only: this host is the ONLY publisher.
    readonly landmineDaysChanged: Observable<LandmineDayMap | null> = this._landmineDaysChanged.asObservable();

    // The facade read: SYNCHRONOUS, because the skill filters synchronously. Null means "no calendar
    // yet", which every reader must treat as UNKNOWN - never as "clear".
    readLandmineDays(): LandmineDayMap | null {
        return this._landmineDays;
    }

    /**
     * Fetch (or re-use) the verdicts covering `span`, and publish the result.
     *
     * Every span reaching here is already QUANTIZED TO WHOLE MONTHS by apg-roll-window.ts, which is
     * what makes the service's exact-equality cache work for us instead of against us: repeated
     * column builds inside the same months, and a run whose window falls in those months, all send
     * the IDENTICAL key. On top of that the host HOLDS the span and only widens it, so the two
     * consumers converge on ONE key even across a month boundary.
     *
     * Called at the PRICE BOX COLUMN BUILD too, which is what makes BR-K1 hold: by the time the
     * trader presses Run the answer is normally already in hand, so he never waits for a lookup.
     *
     * It PUBLISHES on every call, even when the map is unchanged. That is deliberate: the span can
     * be identical while the COLUMNS have changed, and Presentation has to re-apply its colours to
     * the new columns.
     *
     * FAILS LOUDLY (owner ruling 2026-09-08). A calendar failure - the Shell's facts service down,
     * the transport, a malformed reply - is toasted to the trader, DROPS the map we hold (a stale
     * map would paint yesterday's colours as if they were checked), publishes the empty map so the
     * Price Box marks clear, and REJECTS so an ORION run awaiting it fails with the reason. A data
     * gap is not a failure: a map of "unknown" days resolves normally.
     */
    async ensureLandmineDays(span: LandmineSpan | null): Promise<LandmineDayMap | null> {
        // TWO stability steps, in this order. The span arrived already quantized to whole months
        // (predictable, and shared between the run and the column build); holding it then collapses
        // the residual case - a window inside one month against a column set that spills into the
        // next - onto a single key instead of two that alternate.
        const stable = stabiliseSpan(this._landmineSpan, span);

        if (!stable) {
            return this._landmineDays;   // nothing to ask about; keep what we have
        }

        this._landmineSpan = stable;

        try {
            this._landmineDays = await this._landmineDaysService.getDays(stable.from, stable.to);
        } catch (e) {
            this._landmineDays = null;
            this._landmineDaysChanged.next(null);

            const cause = e && e.message ? String(e.message) : String(e);
            this._toastr.error(
                cause + '. Landmine days cannot be verified - no colouring, no ORION run.',
                'Events Calendar');
            throw e;
        }

        this._landmineDaysChanged.next(this._landmineDays);
        return this._landmineDays;
    }

    /**
     * The column-build refresh. The Price Box needs its columns coloured whether or not ORION has
     * ever run, so the span comes from the loaded columns here - quantized to whole months, so the
     * columns moving as data arrives cannot mint a new cache key on every repaint.
     */
    private ensureLandmineDaysForColumns(): void {
        // Already toasted and logged inside ensureLandmineDays; the catch only keeps a fire-and-
        // forget column refresh from surfacing as an unhandled rejection on top of the toast.
        this.ensureLandmineDays(landmineSpanOf(this.priceboxColumnDayKeys()))
            .catch(() => { /* surfaced by ensureLandmineDays */ });
    }

    /**
     * Every expiration currently shown as a Price Box column, as "yyyy-MM-dd" keys. Includes columns
     * whose cells are empty: a day with no tradeable cell still needs its colour, and still needs to
     * be distinguishable from a day the calendar never covered.
     */
    priceboxColumnDayKeys(): string[] {
        const keys: string[] = [];
        const rows = this.priceboxSection ? this.priceboxSection.rows : [];

        rows.forEach(row => {
            (row.columns || []).forEach(col => {
                const parsed = this.parseApgExpiration(col.expirationDatePretty);
                if (!parsed) {
                    return;
                }
                const key = apgDayKey(parsed);
                if (keys.indexOf(key) < 0) {
                    keys.push(key);
                }
            });
        });

        keys.sort();
        return keys;
    }

    // Parse "26-May-23" (dd-MMM-yy) to a Date; null if unparseable.
    private parseApgExpiration(pretty: string): Date | null {
        if (!pretty) {
            return null;
        }
        const parts = pretty.split('-');
        if (parts.length !== 3) {
            return null;
        }
        const day = parseInt(parts[0], 10);
        const monIx = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
            .indexOf(parts[1].toLowerCase());
        let year = parseInt(parts[2], 10);
        if (isNaN(day) || monIx < 0 || isNaN(year)) {
            return null;
        }
        if (year < 100) {
            year = 2000 + year;
        }
        return new Date(year, monIx, day);
    }
    // ===== end APG CoPilot ==============================================================

    // ===== Price Box landmine highlighting (CR-20260904-001, Presentation) ==============
    // PRESENTATION ONLY. This region reads two things — the landmine verdict map published on
    // `landmineDaysChanged`, and the trader's three highlight switches — and produces ONE string per
    // column, which becomes an attribute on a <td>. It reads no run, no order, no position, and
    // nothing in it is reachable from the ORION skill (BR-P3).

    // The per-column answer, keyed by the Price Box's own "dd-MMM-yy" label. Computed here, never in
    // a binding: the template's only entry point is landmineMark(), a dictionary read. '' is a real
    // answer — "no colour" — so a resolved miss is cached like any other.
    private _landmineMarks: { [expirationLabel: string]: string } = {};

    // The last map carried on `landmineDaysChanged`, held for the PAINT.
    //
    // Deliberately this team's own field and not the host's `_landmineDays` (the Filtering region's,
    // above): the ruling is that Presentation repaints from the map the EMISSION carries, never by
    // reaching into another team's state. It is the same instance either way — Filtering publishes
    // exactly what it holds — so the card and the column cannot disagree (contract C1 G7), and the
    // seam stays a seam.
    //
    // HELD so that saving a switch repaints from verdicts already in hand: no fetch, no column
    // rebuild, no ORION run. This team never calls `getDays`.
    private _landmineDaysForPaint: LandmineDayMap | null = null;

    // Snapshot of the three switches. Never read the settings getter from a binding — it JSON-clones
    // the whole APG settings blob on every read, and this grid repaints on every quote batch.
    private _highlightSwitches: LandmineHighlightSwitches = {
        highlightMondays: false,
        highlightTier1: false,
        highlightTier2: false
    };

    /**
     * The join: verdict map + switches -> one mark per column. Runs on an emission of
     * `landmineDaysChanged` (a column build upstream), on a settings save, and never inside a
     * binding.
     *
     * Cause -> effect: the switches are snapshotted HERE, so a switch save repaints against the map
     * already held. Nothing is fetched and nothing about the run is touched.
     */
    @DetectMethodChanges()
    private refreshLandmineHighlights(): void {

        // Read from the PRICE BOX's own settings (owner ruling 2026-09-04: the switches moved out of
        // Application Settings and into the Price Box Section Settings dialog). Persistence is
        // unchanged — `OffsetsManager` and `ApplicationSettingsService` both store through
        // `UserSettingsService` — so this is still one value per user, only edited elsewhere.
        const priceBoxSettings = this._visibleOffsetsService.priceBox;

        this._highlightSwitches = {
            highlightMondays: priceBoxSettings.highlightMondays || false,
            highlightTier1: priceBoxSettings.highlightTier1 || false,
            highlightTier2: priceBoxSettings.highlightTier2 || false
        };

        // O73: the fourth switch. Snapshotted here for the same reason as the three above.
        this._showLandmineEventsTooltip = priceBoxSettings.showLandmineEventsTooltip || false;

        const labels: string[] = [];
        const headers = this.priceboxSection ? this.priceboxSection.columnHeaders : null;
        if (headers) {
            for (let i = 0; i < headers.length; i++) {
                if (headers[i] && headers[i].date) {
                    labels.push(headers[i].date);
                }
            }
        }

        this._landmineMarks = buildLandmineMarks(labels, this._landmineDaysForPaint, this._highlightSwitches);

        // The wording follows the colour: a new map or a switch save can change both.
        this._landmineTooltips = {};
        this._landmineTooltipLabel = null;
    }

    /**
     * The template's ONLY entry point, on the column HEADER <td> and nowhere else (owner ruling
     * 2026-09-04, superseding AC-17: the mark colours the header cell, not the priced body cells).
     * A dictionary read.
     *
     * A label the last refresh did not know about (the columns were rebuilt after the map arrived) is
     * resolved once and memoised, so a column can never render silently uncoloured while the answer
     * is in hand. '' is a real cached answer — "no colour" — and returns null, which REMOVES the
     * attribute rather than leaving an empty one on every uncoloured cell in the grid.
     */
    landmineMark(expirationLabel: string): string | null {

        if (!expirationLabel) {
            return null;
        }

        const known = this._landmineMarks[expirationLabel];
        if (known !== undefined) {
            return known || null;
        }

        const resolved = buildLandmineMarks([expirationLabel], this._landmineDaysForPaint, this._highlightSwitches);
        const mark: string = resolved[expirationLabel] || '';
        this._landmineMarks[expirationLabel] = mark;

        return mark || null;
    }

    // The Price Box is being rebuilt or cleared: drop the marks with it, so no stale colour survives
    // a template change. Same precedent as the CoPilot preview key.
    private resetLandmineHighlights(): void {
        this._landmineMarks = {};
        this._landmineTooltips = {};
        this._landmineTooltipLabel = null;
    }

    // ----- hover tooltip on a coloured header (follow-on 2026-09-08, O70–O72) -----------------
    //
    // Super users only. The tooltip follows the VISIBLE colour: it exists for a header painted
    // tier1 or tier2 and for nothing else. A Monday painted grey because its tier switch is off gets
    // no tooltip (owner-confirmed assumption, 2026-09-08). The wording is read from the SAME held map
    // that painted the colour, so the list and the colour cannot disagree. No fetch, no ORION run.

    // Which header's tooltip is open. One at a time — the mouse is over one cell.
    private _landmineTooltipLabel: string | null = null;

    // O73: the "Events List Tooltip" switch from the Price Box Section Settings. Off = no tooltip
    // even for a super user. Snapshotted in refreshLandmineHighlights(), never read from a binding.
    private _showLandmineEventsTooltip = false;

    // Memoised per label: null is a real answer ("no tooltip") so a miss is cached like a hit.
    private _landmineTooltips: { [expirationLabel: string]: LandmineTooltipContent | null } = {};

    get landmineTooltipLabel(): string | null {
        return this._landmineTooltipLabel;
    }

    /**
     * The tooltip's content for a header, or null when there is none to show. A dictionary read on
     * the hot path; resolved once per label.
     */
    landmineTooltip(expirationLabel: string): LandmineTooltipContent | null {

        if (!expirationLabel || !this._showLandmineEventsTooltip || !this._sessionService.isSuperUser) {
            return null;
        }

        const known = this._landmineTooltips[expirationLabel];
        if (known !== undefined) {
            return known;
        }

        const content = this.resolveLandmineTooltip(expirationLabel);
        this._landmineTooltips[expirationLabel] = content;
        return content;
    }

    private resolveLandmineTooltip(expirationLabel: string): LandmineTooltipContent | null {

        const mark = this.landmineMark(expirationLabel);
        if (mark !== 'tier1' && mark !== 'tier2') {
            return null;
        }

        if (!this._landmineDaysForPaint) {
            return null;
        }

        const date = parsePriceboxExpirationLabel(expirationLabel);
        if (!date) {
            return null;
        }

        const day = this._landmineDaysForPaint.get(priceboxDayKey(date));
        return day ? buildLandmineTooltip(day.events) : null;
    }

    @DetectMethodChanges()
    showLandmineTooltip(expirationLabel: string): void {
        this._landmineTooltipLabel = this.landmineTooltip(expirationLabel) ? expirationLabel : null;
    }

    @DetectMethodChanges()
    hideLandmineTooltip(): void {
        this._landmineTooltipLabel = null;
    }
    // ===== end Price Box landmine highlighting ==========================================

    //
    portfolios: { key: string, userId: string, items: ApgPortfolio[] }[];

    //
    userContext: string;

    //
    isZonesGridLinked = true;

    //
    private _selectedPortfolio: ApgPortfolio;
    get selectedPortfolio(): ApgPortfolio {
        return this._selectedPortfolio;
    }

    @DetectSetterChanges()
    set selectedPortfolio(value: ApgPortfolio) {
        this._selectedPortfolio = value;
    }

    //
    portfolioPopup: {
        isVisible: boolean,
        strategies: CashFlowStrategy[],
        strategy?: CashFlowStrategy,
        name?: string,
        tradingInstrument?: TradingInstrument,
        mode?: 'new' | 'edit',
        // CR-20260928-001: the boot/fallback role (ruling Q3/Q7); any portfolio can carry it.
        isDefault?: boolean,
        // Set by the paste offer (ruling Q2): the pair is dictated by the pasted data.
        lockPair?: boolean,
        hint?: string
    };

    // CR-20260928-001: resolves the paste offer (ruling Q2) when the popup closes — with the created
    // portfolio on Save, with undefined on Cancel.
    private _portfolioPopupResolver: (portfolio: ApgPortfolio | undefined) => void;

    //
    private _positionsColCollapsed: boolean;
    get positionsColCollapsed(): boolean {
        return this._positionsColCollapsed;
    }

    set positionsColCollapsed(v: boolean) {
        this._positionsColCollapsed = v;
    }

    //
    private _settingsColCollapsed: boolean;
    get settingsColCollapsed(): boolean {
        return this._settingsColCollapsed;
    }

    set settingsColCollapsed(v: boolean) {
        this._settingsColCollapsed = v;
    }

    //
    private _strategiesColCollapsed = true;
    get strategiesColCollapsed(): boolean {
        return this._strategiesColCollapsed;
    }

    set strategiesColCollapsed(v: boolean) {
        this._strategiesColCollapsed = v;
    }

    //
    private _priceboxColCollapsed: boolean;
    get priceboxColCollapsed(): boolean {
        return this._priceboxColCollapsed;
    }

    set priceboxColCollapsed(v: boolean) {
        this._priceboxColCollapsed = v;
    }

    private _zonesGridColCollapsed = true;
    get zonesGridColCollapsed(): boolean {
        return this._zonesGridColCollapsed;
    }

    set zonesGridColCollapsed(v: boolean) {
        this._zonesGridColCollapsed = v;
    }

    //
    private _isDataLoading: boolean;
    get isDataLoading(): boolean {
        return this._isDataLoading;
    }

    @DetectSetterChanges()
    set isDataLoading(v: boolean) {
        this._isDataLoading = v;
    }

    private readonly _pendingRequestId: string[] = [];

    // ⚠ WHAT WAS HERE UNTIL 2026-09-10 (CR-20260910-001 milestone 1): `_copilotPending`, its
    // give-up timer, `_copilotLastRunWarnings` and `readLastRunWarnings()`. They correlated the
    // 'copilot-' tagged `GetAdjustmentSolutions` reply back to an awaiting ORION run and carried the
    // backend's notes off it. THE RUN NO LONGER FIRES A PRICING CALL: it sends `RunApgOrionSkill`,
    // the result arrives on the event stream correlated by its own runId, and the backend's warnings
    // ride on that same delivery. Nothing here is in flight for ORION any more.

    //
    private _positionSectionHeight = 0;

    // noinspection JSUnusedGlobalSymbols
    get leftColSettingsSectionHeight() {
        return `calc(100% - ${this._positionSectionHeight}px)`;
    }

    get fullyCollapsed(): boolean {
        return !this.isSectionOpen('strategies') && !this.isSectionOpen('pricebox') && !this.isSectionOpen('zones-grid');
    }

    // Section visibility (the burger in the PORTFOLIOS header). Persisted per panel type — one record
    // for every APG instance, one per side for the Comparison Grid. Portfolios is never on the list.
    private _sectionVisibility: ApgSectionVisibility = makeDefaultSectionVisibility();

    // Entitlement stays on top: a section the backend has not granted is hidden whatever the toggle says.
    isSectionVisible(col: ApgGridColumn): boolean {
        if (col === 'positions') {
            return true;
        }
        if (col === 'zones-grid' && !this.isZonesGridAvailable) {
            return false;
        }
        return this._sectionVisibility[col] !== false;
    }

    // "Open" = visible AND expanded. A hidden section counts as collapsed for width purposes
    // (open question 1, default taken).
    isSectionOpen(col: ApgGridColumn): boolean {
        if (!this.isSectionVisible(col)) {
            return false;
        }
        switch (col) {
            case 'positions':
                return !this.positionsColCollapsed;
            case 'settings':
                return !this.settingsColCollapsed;
            case 'strategies':
                return !this.strategiesColCollapsed;
            case 'pricebox':
                return !this.priceboxColCollapsed;
            case 'zones-grid':
                return !this.zonesGridColCollapsed;
        }
        return false;
    }

    private applySectionVisibility(visibility: ApgSectionVisibility) {
        this._sectionVisibility = visibility;
        // Sibling grids (the comparison panel) recompute their width off this event.
        this.sectionCollapsed$.emit({section: 'settings', state: this.settingsColCollapsed});
        this._changeDetector.detectChanges();
    }

    showSectionVisibilityMenu() {
        this.sectionVisibilityMenu.show();
    }

    //
    get isAtmOutdated(): boolean {
        return this.strategiesSection
            .strategies
            .flatMap(x => x.adjustmentTypes)
            .some(x => x.isAtmStrikeOutdated);
    }

    //
    readonly settingsSection: SettingsSectionModel;

    //
    readonly strategiesSection: StrategiesSectionModel;

    //
    readonly positionsSection: PositionsSectionModel;

    //
    readonly priceboxSection: PriceboxSectionModel;

    //

    @ViewChild('portfolioDelete')
    portfolioDeleteConfirmationDialog: GenericConfirmationDialogComponent;

    lastUsedSettings: AdjustmentPricingSettingsDto;


    //
    get cssForPositionsSection(): string[] {
        const css = [];

        if (this.showHedges) {
            css.push('show-hedges');
        }

        if (this.positionsSection.savedStateLoaded) {
            css.push('saved-data-badge');
        }

        if (!this.showPortfoliosSection) {
            css.push('full');
        }

        return css;
    }

    //
    get cssForSettingsSection(): string[] {
        const css = [];

        if (this.positionsSection.savedStateLoaded) {
            css.push('saved-data-badge');
        }

        return css;
    }

    //
    get isOwnUserContext(): boolean {
        return this.userContext === this._sessionService.sessionData.userId;
    }

    //
    get showPortfoliosSection(): boolean {
        return this._accessControlService.isSecureElementAvailable('a7fc7492-2509-455c-97c3-4d5b257a50a1');
    }

    //
    // CR-20260928-001, ruling Q3: the DEFAULT portfolio cannot be deleted while it is the default
    // (mark another one first), and the LAST remaining portfolio cannot be deleted either — a
    // fallback must always exist.
    get canDeletePortfolio(): boolean {
        if (isVoid(this.selectedPortfolio) || !this.isOwnUserContext) {
            return false;
        }
        if (this.selectedPortfolio.isDefault) {
            return false;
        }
        return this.getCurrentUserPortfolios().length > 1;
    }

    //
    get canCreatePortfolio(): boolean {
        return this.isOwnUserContext;
    }

    // Rename + the default flag; any own portfolio, the default included.
    get canEditPortfolio(): boolean {
        return !isVoid(this.selectedPortfolio) && this.isOwnUserContext;
    }

    //
    get canRefreshPortfolio(): boolean {
        return !isVoid(this.selectedPortfolio) && !this.isOwnUserContext;
    }

    get shouldApplyZonesGrid(): boolean {
        return !isVoid(this.zonesGridSection) && this.isZonesGridLinked
            && !isVoid(this.zonesGridSection.loadedRow);
    }

    //
    @DetectMethodChanges()
    ngOnInit(): void {

        this._sectionVisibility = this._sectionVisibilityService.getVisibility(this.orientation);

        // A save in ANY grid of the same kind (same key) lands here: other APG panels, or this side
        // of the comparison grid. The other side has its own key and is filtered out.
        this._sectionVisibilityService.changed$
            .pipe(
                takeUntil(this._unsubscriber),
                filter(x => x.orientation === this.orientation)
            )
            .subscribe(x => this.applySectionVisibility(x.visibility));

        this._messageBus.of<StrategyPriceDto[]>('StrategyPriceDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onStrategyPrice(x.payload));

        // ORION "today" source in a live shell (readTodayKey). Stored only; nothing repaints on it.
        this._messageBus.of<ServerTimeMessageDto>('ServerTimeMessageDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => { this._lastServerTime = x.payload; });

        this._messageBus.of<AtmStrikeChangedEvent>('AtmStrikeChangedEvent')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onAtmStrikeChanged(x.payload));

        // CR-20260922-001 — an Action Board card asked for this portfolio (live path; the boot path
        // reads the cached last request once the portfolios are filled, see ngAfterViewInit).
        this._messageBus.of<OpenSubjectRequest>(APG_SELECT_PORTFOLIO_REQUEST_TOPIC)
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => {
                const takes = !isVoid(this.portfolios) && this.takesSelectRequest(x.payload);
                console.log(`[apg][zone-drift] live select request: panel=${this.panelId} target=${x.payload?.targetPanelId}`
                    + ` portfoliosLoaded=${!isVoid(this.portfolios)} takes=${takes} zoneDrift=${JSON.stringify(x.payload?.zoneDrift)}`);
                if (takes) {
                    this.applySelectPortfolioRequest(x.payload).catch(e => console.error('[apg] select request failed', e));
                }
            });

        this._messageBus.of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onQuote(x.payload));

        this._messageBus.of<TrackingStartedMessage>('TrackingStartedMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onTrackingStartedConfirmation(x.payload));

        this._messageBus.of<TrackingStoppedMessage>('TrackingStoppedMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onTrackingStoppedConfirmation(x.payload));

        this._messageBus.of<TrackingRequestErrorMessage>('TrackingRequestErrorMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onTrackingRequestError(x.payload));

        this._messageBus.of<TrackingTemplateUpdated>('TrackingTemplateUpdated')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onTrackingTemplateUpdated(x.payload));

        this._messageBus.of<TrackingNotificationSettingsUpdated>('TrackingNotificationSettingsUpdated')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onTrackingNotificationSettingsUpdated(x.payload));

        this._messageBus.of<ApgPortfolioStateChangedDto>('ApgPortfolioStateChangedDto')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(x => x.payload.userId !== this._sessionService.sessionData.userId)
            )
            .subscribe(x => this.onSubordinatePortfolioStateChanged(x.payload));

        // An explicit "set as current zone" from the footer indicator. The ORION Zone step reads
        // the cache written by refreshCurrentZone (portfolio selection only), so without this the
        // new zone showed up only after a portfolio reload. Carries the value; no re-query.
        this._messageBus.of<CurrentZoneChangedMessage>('CurrentZoneChanged')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(x => !isVoid(this._selectedPortfolio)
                    && x.payload.portfolioKey === makePortfolioKey(this._selectedPortfolio))
            )
            .subscribe(x => this.onCurrentZoneChanged(x.payload.currentZone));

        // CR-20260918-005: the day flipped (QG replay cursor stepped or jumped to another day).
        // The shell re-evaluates the selected portfolio's zone for that day; re-read it the same
        // way a portfolio selection does, so the ORION Zone step reads the cursor day's zone.
        this._shellToday.changed$
            .pipe(
                takeUntil(this._unsubscriber),
                filter(() => !isVoid(this._selectedPortfolio))
            )
            .subscribe(() => this.refreshCurrentZone(this._selectedPortfolio));

        this._messageBus.of<ApgPortfolioStateChangedDto>('Apg.ClientSavedPositionsChanged')
            .pipe(
                takeUntil(this._unsubscriber),
            )
            .subscribe(x => this._changeDetector.detectChanges());

        this._messageBus.of<ApgPortfolioStateChangedDto>('Apg.LastUsedTemplateChanged')
            .pipe(
                takeUntil(this._unsubscriber),
            )
            .subscribe(x => this._changeDetector.detectChanges());

        const mediaQuery = window.matchMedia('( max-width: 1024px )');

        if (mediaQuery.matches) {
            this.priceboxColCollapsed = true;
        }

        if (!isVoid(this.orientation)) {
            this._positionsColCollapsed = false;
            this._strategiesColCollapsed = true;
            this._priceboxColCollapsed = true;
        }


        this._messageBus.of<SweetPriceAvailableDto>('SweetPriceAvailableDto')
            .pipe(
                takeUntil(this._unsubscriber),
            )
            .subscribe(x => this.onSweetPriceAvailable(x.payload));


        this._messageBus.of<GetAdjustmentSolutionsReply>('GetAdjustmentSolutionsReply')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(async x => {
                await this.onDataLoaded(x.payload);
            });

        this._messageBus.of<any>(AppSettingsUpdatedMessageTopic)
            .subscribe(() => this.throttleApplyLiveUpdates());

        this.throttleApplyLiveUpdates();

        this._messageBus.of<any>('Qg.NextQuoteGenerated')
            .subscribe(msg => this.onQuoteGeneratorQuote(msg.payload));

        // ---- Price Box landmine highlighting (CR-20260904-001, Presentation) ----------------
        // Trigger 1: a landmine map was published by the Filtering region's `ensureLandmineDays`.
        // This is the ONLY way this team learns of a verdict — it never calls `getDays`, so the card
        // and the column always read the same map instance.
        //
        // The subject is a BehaviorSubject (leader's ruling on Q-D), so this subscription is handed
        // the last map even when it is created after the publish. `null` is a REAL emission — the
        // seed, and the state before the first column build — and means "no calendar yet": Mondays
        // still grey, no tier colour. It is not an error and must not be treated as one.
        //
        // Filtering publishes on EVERY call, not only when the map is refetched, because the span can
        // be identical while the COLUMNS have moved. That is exactly what this repaint needs: it
        // rebuilds the marks from the column set as it stands at the emission, and the publish comes
        // after `priceboxSection.setData(...)`, so the columns are already the new ones.
        this.landmineDaysChanged
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(map => {
                this._landmineDaysForPaint = map || null;
                this.refreshLandmineHighlights();   // replaces the mark dictionary wholesale
            });

        // Trigger 2: the trader saved a switch in the Price Box Section Settings dialog — repaint
        // from the map already held. No fetch, no Apply, no ORION run (BR-P3).
        //
        // `priceBoxUpdated$` is the seam the Price Box's own settings already use — `PriceboxSectionModel`
        // re-reads Show IV / Zones Grid Arrow / Pricing Steps Icon on the very same event. It fires
        // once per setter, so one Save emits several times; that is the existing behaviour of this
        // event and each repaint is a dictionary rebuild over the visible columns.
        this._visibleOffsetsService.priceBoxUpdated$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.refreshLandmineHighlights());

        // Snapshot the switches for the first paint. Kept as a second layer even though a
        // BehaviorSubject emits its seed on subscribe: the guarantee lives in another team's member,
        // and if it ever became a ReplaySubject(1) with nothing published yet there would be no
        // emission at all, and the switches would go unread until one arrived.
        this.refreshLandmineHighlights();
    }

    private throttleApplyLiveUpdates() {
        if (this.applyLiveAttempted$) {
            this.applyLiveAttempted$.complete();
        }
        this.applyLiveAttempted$ = new Subject<void>();
        this.applyLiveAttempted$
            .pipe(debounceTime((this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates || 0) * 1000))
            .subscribe({
                next: async x => await this.applyClicked(),
                complete: () => {
                    console.log('completed, app settings updated')
                }
            });
    }

    onSweetPriceAvailable(payload: SweetPriceAvailableDto): void {
        let exp = makeGuiFriendlyExpirationDate(payload.expiration);
        const msg = `Expiration: ${exp}, Price: $${payload.price}, Adjustment: ${payload.adjustment}`;
        this._toastr.info(msg, 'Sweet Price Available!');
    }

    //
    private onSubordinatePortfolioStateChanged(payload: ApgPortfolioStateChangedDto): void {
        const userPortfolios = this.getPortfoliosByUser(payload.userId);

        const updatedPortfolio = userPortfolios.find(x => x.id === payload.apgPortfolioId);

        if (isVoid(updatedPortfolio)) {
            return;
        }

        if (updatedPortfolio === this.selectedPortfolio) {
            this._changeDetector.detectChanges();
            this._portfoliosService.savePortfolios(userPortfolios);
        }
    }

    //
    @DetectMethodChanges({isAsync: true})
    async ngAfterViewInit(): Promise<void> {

        // Fill portfolios

        this.isDataLoading = true;

        try {

            const grps = this._sessionService.sessionData.users
                .sort((a, b) => a.userName.localeCompare(b.userName))
                .map(user => {

                    const grp = {
                        key: user.userName,
                        userId: user.userId,
                        items: []
                    };

                    const cfg: ServiceConfiguration = {
                        orientation: this.orientation,
                        userId: user.userId,
                        userName: user.userName
                    };

                    this._portfoliosService.configure(cfg);

                    const pfs = this._portfoliosService.getPortfolios();

                    grp.items.push(...pfs);

                    return grp;
                });

            const ownUser = this.getUserObject(this._sessionService.sessionData.userId);

            await this.configureServices({
                orientation: this.orientation,
                userId: ownUser.userId,
                userName: ownUser.userName
            });

            const pfs = this._portfoliosService.getPortfolios();

            const ownGrp = {
                key: ownUser.userName,
                userId: ownUser.userId,
                items: pfs
            }

            grps.unshift(ownGrp);

            this.portfolios = grps;

            if (!this._sessionService.isSuperUser) {
                await this.selectBootPortfolio();
            }

            this.priceBoxMenu.settingsSaved$
                .pipe(takeUntil(this._unsubscriber))
                .subscribe(_ => this._changeDetector.detectChanges());

        } finally {

            this.isDataLoading = false;

        }

        // CR-20260922-001 — a panel added BY a deep link boots after the request was published: take
        // the cached one if it is fresh, then clear it so a later manual open does not replay it.
        const pending = this._messageBus.getLastMessage<OpenSubjectRequest>(APG_SELECT_PORTFOLIO_REQUEST_TOPIC);
        if (pending && pending.payload) {
            console.log(`[apg][zone-drift] boot: cached select request age=${Date.now() - pending.payload.at}ms target=${pending.payload.targetPanelId}`
                + ` panel=${this.panelId} zoneDrift=${JSON.stringify(pending.payload.zoneDrift)}`);
        }
        if (pending && pending.payload && Date.now() - pending.payload.at < SELECT_REQUEST_TTL_MS && isVoid(this.orientation)
            && this.takesSelectRequest(pending.payload)) {
            this._messageBus.clearLastMessage(APG_SELECT_PORTFOLIO_REQUEST_TOPIC);
            await this.applySelectPortfolioRequest(pending.payload);
        }
    }

    /** CR-20260924-001 F4b: ONE panel takes a deep link — the one the host stamped, or a panel
     * being added now (unstamped). Every other open APG ignores it. */
    private takesSelectRequest(request: OpenSubjectRequest): boolean {
        return !!request
            && (!request.sourceLayoutTabId || request.sourceLayoutTabId === this.layoutTabId)
            && (!request.targetPanelId || request.targetPanelId === this.panelId);
    }

    // CR-20260922-001. Loads the requested portfolio the way the dropdown would (synthetic 'ets'
    // event). Only portfolios this panel lists (own + direct subordinates') can be loaded; anything
    // else is reported, never guessed. No zone is pre-set: the zone is read, never set, by a caller.
    private async applySelectPortfolioRequest(request: OpenSubjectRequest): Promise<void> {
        if (!request || !isVoid(this.orientation)) {
            return;
        }

        const portfolio = this.getPortfoliosByUser(request.userId).find(x => x.id === request.portfolioId);

        if (isVoid(portfolio)) {
            this._toastr.warning('The portfolio the Action Board asked for is not loaded in this panel');
            return;
        }

        const alreadySelected = this._selectedPortfolio && this._selectedPortfolio.id === portfolio.id
            && this._selectedPortfolio.userId === portfolio.userId;

        const zone = request.zoneDrift && isValidNumber(request.zoneDrift.zone, true)
            ? request.zoneDrift.zone
            : null;

        console.log(`[apg][zone-drift] select request taken: portfolio=${portfolio.id} alreadySelected=${!!alreadySelected} zone=${zone}`);

        if (!alreadySelected) {
            // CR-20260927-003: the portfolio load kicks an UNAWAITED async chain (template select ->
            // 250 ms -> symbol -> strategy -> positions -> template apply -> onTemplateApplied). The
            // one-shot must run AFTER it, so it is armed here and fired by onTemplateApplied.
            if (zone !== null) {
                this.armZoneDrift(zone, {template: true, active: !this._panelIsActive});
            }
            this.selectedPortfolio = portfolio;
            await this.onPortfolioSelected({previousValue: undefined, value: portfolio, event: 'ets'});
        } else if (zone !== null) {
            // Already loaded: no template chain to wait for - but the grid may still be HIDDEN
            // (the trader clicked on the Action Board tab; the host activates this panel right
            // after publishing the request). Smoke 2 showed the dialog opening on a hidden grid
            // and never appearing. Fire on activation, or at once when already on screen.
            this.armZoneDrift(zone, {template: false, active: !this._panelIsActive});
        }
    }

    // CR-20260927-003: the zone-drift one-shot waiting for the portfolio's template to be applied.
    // Fires only when EVERY awaited condition has been met: 'template' = the portfolio's template
    // chain finished (onTemplateApplied); 'active' = the wrapper panel put the grid on screen.
    // Smoke 3 regressed path (a) because activation alone fired it mid-chain.
    private _zoneDriftArmed: { zone: number, timer: any, waitFor: Set<'template' | 'active'> } | null = null;

    private armZoneDrift(zone: number, wait: { template: boolean, active: boolean }): void {
        this.disarmZoneDrift('re-armed');
        const waitFor = new Set<'template' | 'active'>();
        if (wait.template) { waitFor.add('template'); }
        if (wait.active) { waitFor.add('active'); }
        // Safety net: if an awaited hook never comes (template void, load failed, panel never
        // activated), run anyway after a bounded wait - a validation toast beats silence.
        const timer = setTimeout(() => {
            if (this._zoneDriftArmed && this._zoneDriftArmed.timer === timer) {
                console.warn(`[apg][zone-drift] still waiting for [${Array.from(this._zoneDriftArmed.waitFor)}] after 15 s - running anyway`);
                this.fireArmedZoneDrift('timeout');
            }
        }, 15000);
        this._zoneDriftArmed = {zone, timer, waitFor};
        console.log(`[apg][zone-drift] armed: zone=${zone} waitFor=[${Array.from(waitFor)}]`);
        if (waitFor.size === 0) {
            this.fireArmedZoneDrift('nothing to wait for');
        }
    }

    private zoneDriftConditionMet(condition: 'template' | 'active', trigger: string): void {
        const armed = this._zoneDriftArmed;
        if (!armed || !armed.waitFor.has(condition)) {
            return;
        }
        armed.waitFor.delete(condition);
        console.log(`[apg][zone-drift] ${trigger}; still waiting for [${Array.from(armed.waitFor)}]`);
        if (armed.waitFor.size === 0) {
            this.fireArmedZoneDrift(trigger);
        }
    }

    private disarmZoneDrift(reason: string): void {
        if (!this._zoneDriftArmed) {
            return;
        }
        clearTimeout(this._zoneDriftArmed.timer);
        this._zoneDriftArmed = null;
        console.log(`[apg][zone-drift] disarmed: ${reason}`);
    }

    private fireArmedZoneDrift(trigger: string): void {
        const armed = this._zoneDriftArmed;
        if (!armed) {
            return;
        }
        this.disarmZoneDrift('firing (' + trigger + ')');
        // Let the panel's own change detection (1 ms delayed in PanelBaseComponent) and the
        // template-apply chain settle, so the grid is on screen before the dialog opens.
        setTimeout(() => {
            this.runZoneDriftOneShot(armed.zone).catch(e => console.error('[apg][zone-drift] one-shot failed', e));
        }, 50);
    }

    // CR-20260927-003 (owner rulings 2026-09-27). A zone-drift card means the shorts are off the
    // zone, so the ONE adjustment that moves the book into it is #2B(C)+#2B(P) - the same choice
    // ORION's selector makes for that fact. This emulates an Apply for that one run:
    //   - the Override ATM dialog ALWAYS shows, pre-filled with the card's zone, UNROUNDED (the
    //     engine resolves strikes); "Use Market ATM" = market ATM, still #2B only;
    //   - the request carries the #2B filter in place of the saved favourites, for this run only;
    //   - nothing is persisted; the next plain Apply prices the normal full set, and the Apply
    //     button is left hot so no edit is needed for it.
    // The dialog is modal, so nothing can be edited while it is open; the mode ends with the run.
    private async runZoneDriftOneShot(zone: number): Promise<void> {
        const tpl = this.settingsSection.globalSettings.selectedTemplate;
        console.log(`[apg][zone-drift] one-shot start: zone=${zone} portfolio=${this._selectedPortfolio?.id}`
            + ` template=${tpl?.templateName} strategy=${this.settingsSection.globalSettings.selectedStrategy}`
            + ` positions=${(this.positionsSection.positionsData || []).map(x => x.positions.length).join('/')}`
            + ` orionHold=${this.orionHoldsGrid} dialog=${!!this.overrideAtmDialog}`);

        if (this.orionHoldsGrid) {
            this._toastr.warning('ORION holds the grid. Reset ORION to price the move into the zone.');
            return;
        }

        const positionsErrors = this.positionsSection.validate();
        if (positionsErrors.length > 0) {
            console.warn('[apg][zone-drift] positions validation failed', positionsErrors);
            positionsErrors.forEach(e => this._toastr.error(e, 'Positions'));
            return;
        }

        const settingsErrors = this.settingsSection.validate();
        if (!isVoid(settingsErrors)) {
            console.warn('[apg][zone-drift] settings validation failed', settingsErrors);
            settingsErrors.forEach(e => this._toastr.error(e, 'Settings'));
            return;
        }

        this._zoneDriftOneShot = {zone};

        try {
            try {
                const shown = this.overrideAtmDialog.show(zone);
                this._changeDetector.detectChanges();
                console.log('[apg][zone-drift] override ATM dialog shown, prefilled with ' + zone);
                const overrideAtm = await shown;
                console.log('[apg][zone-drift] dialog: Use This ATM = ' + overrideAtm);
                if (!isValidNumber(overrideAtm)) {
                    this._toastr.error('Incorrect Override Atm', 'Error');
                    throw new Error();
                }
                this.settingsSection.globalSettings.overrideAtm = overrideAtm;
            } catch {
                // "Use Market ATM": the box stays empty = market ATM (the trader's call).
                console.log('[apg][zone-drift] dialog: Use Market ATM');
                this.settingsSection.globalSettings.overrideAtm = null;
                if (this.positionsSection.liveStrikesUpdate) {
                    this.positionsSection.overrideAtmLiveMode = 'market';
                }
            } finally {
                if (this.positionsSection.liveStrikesUpdate
                    && isValidNumber(this.settingsSection.globalSettings.overrideAtm)) {
                    this.positionsSection.overrideAtmLiveMode = 'custom';
                }
            }

            this.isDataLoading = true;
            try {
                await this.loadData();
                console.log('[apg][zone-drift] pricing request sent');
            } catch (e) {
                console.error('[apg][zone-drift] pricing request failed', e);
                this._toastr.error('"Load Adjustments Pricing Data" operation completed with errors');
                if (!this.shouldApplyZonesGrid) {
                    this.isDataLoading = false;
                }
            }
        } finally {
            this._zoneDriftOneShot = null;
            // Leave Apply hot: loadData's reset cleared the change flags, and the owner wants the
            // next Apply (normal full set) to run without any edit first.
            this.positionsSection.hasChanges = true;
        }
    }

    private async configureServices(cfg: ServiceConfiguration) {
        this._priceboxPinnedRowsService.configure(cfg);
        this._portfoliosService.configure(cfg);
        this._savedPositionsService.configure(cfg);
        this._positionsStateService.configure(cfg);
        this._copyOrdersToService.configure(cfg);
        this._positionsAfterStateService.configure(cfg);
        await this._cashFlowStrategyTemplatesService.configure(cfg);
    }

    //
    ngOnDestroy(): void {

        if (this._unsubscriber) {
            this._unsubscriber.next();
            this._unsubscriber.complete();
        }

        this.resetStateFull();
        this.resetZonesGrid();
    }


    @DetectMethodChanges()
    showPopup(pricings: CashFlowAdjustment | CashFlowAdjustment[], source: 'PriceBox' | 'Zones Grid') {

        if (isVoid(pricings)) {
            this._toastr.error('Selected Adjustment Cannot be Identified');
            return;
        }

        let p: CashFlowAdjustment[];

        if (!Array.isArray(pricings)) {
            p = [pricings];
        } else {
            p = pricings;
        }

        const hasNa = p.some(x => isNullOrUndefined(x.price) || isNaN(x.price));

        if (hasNa) {
            if (!this._sessionService.isSuperUser) {
                return;
            }
        }

        const strategyType = this.settingsSection.isTwoSideStrategy
            ? 'double'
            : 'single';

        const pfId = this._selectedPortfolio
            ? this._selectedPortfolio
            : null;

        this.solutionPopupCmp.show(pfId, p, strategyType, source);
    }

    get shouldRequestOverrideAtm(): boolean {
        const mode = this._applicationSettingsService.adjustmentPricingGrid.atmWarningMode;

        if (mode !== 'Popup') {
            return false;
        }

        if (this.positionsSection.liveStrikesUpdate) {

            if (this.positionsSection.overrideAtmLiveMode === 'market') {
                return false;
            }

            return !isValidNumber(this.settingsSection.globalSettings.overrideAtm);

        } else {

            return !isValidNumber(this.settingsSection.globalSettings.overrideAtm);

        }
    }

    @DetectMethodChanges({isAsync: true})
    async applyClicked(forceApply?: boolean): Promise<void> {

        // CR-20260927-003 ruling 5: a manual Apply cancels a waiting zone-drift one-shot.
        this.disarmZoneDrift('manual Apply');

        if (this.orionHoldsGrid) {
            this._toastr.warning('ORION holds the grid. Reset ORION to apply manually.');
            return;
        }

        if (!this.canApplyChanges()) {
            this._toastr.warning('No changes to apply');
            return;
        }

        const positionsErrors = this.positionsSection.validate();

        if (positionsErrors.length > 0 && !forceApply) {
            positionsErrors.forEach(e => this._toastr.error(e, 'Positions'));
            return;
        }

        const errors = this.settingsSection.validate();
        if (!isVoid(errors)) {
            errors.forEach(err => this._toastr.error(err, 'Settings'));
            return;
        }

        if (this.shouldRequestOverrideAtm) {

            try {

                const overrideAtm = await this.overrideAtmDialog.show();

                if (!isValidNumber(overrideAtm)) {
                    this._toastr.error('Incorrect Override Atm', 'Error');
                    throw new Error();
                }

                this.settingsSection.globalSettings.overrideAtm = overrideAtm;

            } catch {
                if (this.positionsSection.liveStrikesUpdate) {
                    this.positionsSection.overrideAtmLiveMode = 'market';
                }
            } finally {
                if (this.positionsSection.liveStrikesUpdate) {
                    if (isValidNumber(this.settingsSection.globalSettings.overrideAtm)) {
                        this.positionsSection.overrideAtmLiveMode = 'custom';
                    }
                }
            }
        } else {
            if (this.positionsSection.liveStrikesUpdate) {
                if (isValidNumber(this.settingsSection.globalSettings.overrideAtm)) {
                    this.positionsSection.overrideAtmLiveMode = 'custom';
                }
            }
        }

        this.isDataLoading = true;

        try {

            await this.loadData();

        } catch (e) {

            this._toastr.error('"Load Adjustments Pricing Data" operation completed with errors');

            if (!this.shouldApplyZonesGrid) {
                this.isDataLoading = false;
            }
        }
    }


    private async loadData(): Promise<void> {

        const adjustmentPricingSettings = await this.getAdjustmentPricingSettingsObject('Price Box');

        if (!isVoid(adjustmentPricingSettings.errors)) {
            adjustmentPricingSettings.errors.forEach(x => this._toastr.error(x));
            return;
        }

        // CR-20260927-003: a zone-drift one-shot is tagged so the reply is shown unfiltered (its
        // rows were chosen by the rule, not by the saved favourites) - the same treatment as ORION.
        const requestId = (this._zoneDriftOneShot ? 'zone-drift-' : '') + getShortUUID();

        this._pendingRequestId.push(requestId);

        const qry = new GetAdjustmentSolutions(requestId, adjustmentPricingSettings.settings);

        this.resetStateFull();

        try {
            await this._shellClient.processCommand(qry);

            if (!isVoid(adjustmentPricingSettings)) {
                if (!isVoid(adjustmentPricingSettings.settings)) {
                    this.priceboxSection.lastUsedSettings = adjustmentPricingSettings.settings;
                    this.lastUsedSettings = adjustmentPricingSettings.settings[0];
                }
            }
        } catch {
            const ix = this._pendingRequestId.indexOf(requestId);
            if (ix >= 0) {
                this._pendingRequestId.splice(ix, 1);
            }
        }

        if (this.shouldApplyZonesGrid) {
            await this.loadDataZonesGrid();
        }
    }


    private onStrategyPrice(prices: StrategyPriceDto[]) {

        // let haveToUpdate: boolean;
        let updates = this._strategyCodeIndex.onStrategyPrice(prices)

        if (isVoid(updates)) {
            return;
        }

        // TODO: provide layout id
        this._messageBus.publishAsync({
            topic: 'Apg.StrategyPriceUpdated',
            payload: updates,
        });
    }


    private resetStateFull() {

        this.positionsSection.reset();

        this.settingsSection.reset();

        this.resetPricingData();
    }

    private resetZonesGrid() {
        if (!this.zonesGridSection) {
            return;
        }

        this.zonesGridSection.reset();
        this._quoteIndex.resetZones();
        this._strategyCodeIndex.resetZones();
    }

    private resetPricingData() {

        this._quoteIndex.reset();

        this._strategyCodeIndex.reset();

        this.strategiesSection.reset();

        this.priceboxSection.reset();

        this.solutionPopupCmp.reset();

        // The Price Box is being rebuilt — drop any advisory CoPilot highlight pointing at a stale cell.
        this._copilotPreviewKey = null;

        // ... and the landmine colours with it, so no stale colour outlives the columns it described
        // (CR-20260904-001, Presentation).
        this.resetLandmineHighlights();
    }


    getPriceColorClass(price: number) {
        if (!price) {
            return undefined;
        }

        if (price > 0) {
            return 'credit';
        }

        if (price < 0) {
            return 'debit';
        }

        return undefined;
    }


    async getRefreshPortfolioCss(): Promise<string[]> {

        if (isVoid(this.selectedPortfolio)) {
            return null;
        }

        if (this.isDataLoading) {
            return null;
        }

        const hasTheirChanges = this.hasTheirChanges();

        const hasOwnChanges = this.hasOwnChanges();

        const result = [];

        if (hasOwnChanges) {
            result.push('own-changes');
        }

        if (hasTheirChanges) {
            result.push('their-changes');
        }

        return result;
    }

    hasTheirChanges() {
        if (this.userContext === this._sessionService.sessionData.userId) {
            return false;
        }

        if (isVoid(this.selectedPortfolio)) {
            return false;
        }

        if (!this.settingsSection.isTemplateSelected) {
            return false;
        }

        if (this._longRunningOperationClients > 0) {
            return false;
        }


        const currentTemplate = this._apgOriginalClientPositionsService
            .getTheirCurrentTemplate(this._selectedPortfolio);

        const snapshotTemplate = this._apgOriginalClientPositionsService
            .getTheirSnapshotTemplate(this._selectedPortfolio);

        const templatesEqual = currentTemplate?.templateId === snapshotTemplate?.templateId
            && currentTemplate?.strategy === snapshotTemplate?.strategy
            && currentTemplate.underlying === snapshotTemplate.underlying;

        if (!templatesEqual) {
            return true;
        }

        const originalPositions = this._apgOriginalClientPositionsService.getTheirOriginalPositions(
            this._selectedPortfolio,
        ) || [];

        const currentPositions = this._apgOriginalClientPositionsService
            .getTheirCurrentPositions(this._selectedPortfolio) || [];

        if (isVoid(currentPositions)) {
            return false;
        }

        const originalIds = originalPositions
            .flatMap(x => x.map(y => `${y.ticker}|${y.qty}`))
            .sort();

        const currentIds = currentPositions
            .flatMap(x => x.map(y => `${y.ticker}|${y.qty}`))
            .sort();

        const areEqual = arraysEqual(originalIds, currentIds);

        return !areEqual;
    }


    hasOwnChanges() {
        if (this.userContext === this._sessionService.sessionData.userId) {
            return false;
        }

        if (isVoid(this.selectedPortfolio)) {
            return false;
        }

        if (!this.settingsSection.isTemplateSelected) {
            return false;
        }

        if (this._longRunningOperationClients > 0) {
            return false;
        }

        const theirTemplate = this._apgOriginalClientPositionsService
            .getTheirSnapshotTemplate(this._selectedPortfolio);

        const currentTemplate = this.settingsSection.globalSettings.selectedTemplate;

        const templatesEqual = theirTemplate?.templateId === currentTemplate?.templateId
            && theirTemplate?.underlying === currentTemplate?.underlying
            && theirTemplate?.strategy === currentTemplate?.strategyName

        if (!templatesEqual) {
            return true;
        }

        const their = this._apgOriginalClientPositionsService.getTheirOriginalPositions(
            this._selectedPortfolio,
        ) || [];

        const currentPositions = this.positionsSection.positionsData?.map(x => x.positions) || [];

        const originalIds = their
            .flatMap(x => x.map(y => `${y.ticker}|${y.qty}`))
            .sort();

        const currentIds = currentPositions
            .flatMap(x => x.map(y => `${y.getTicker()}|${y.qty}`))
            .sort();

        const areEqual = arraysEqual(originalIds, currentIds);

        return !areEqual;
    }


    isNullOrUndefined(value: any) {
        return isNullOrUndefined(value) || isNaN(value);
    }


    onAtmStrikeChanged(payload: AtmStrikeChangedEvent): void {

        let needUpdate = false;

        this.strategiesSection.strategies.forEach(str => {
            str.adjustmentTypes.forEach(adjType => {

                if (adjType.isEmpty) {
                    return;
                }

                if (adjType.underlying !== payload.underlying) {
                    return;
                }

                const oldValue = adjType.isAtmStrikeOutdated;

                adjType.isAtmStrikeOutdated = adjType.atmStrike !== payload.currentAtm;

                if (adjType.isAtmStrikeOutdated != oldValue) {
                    needUpdate = true;
                }
            });
        });

        this.positionsSection.updateAtmDistance();

        if (needUpdate) {
            this._changeDetector.detectChanges();
        }

        if (this.positionsSection.liveStrikesUpdate) {
            if (this._sessionService.loginResult.isQuoteGeneratorShell) {
                if (this._isWaitingAtmChangeEvent) {
                    if (!isVoid(this.selectedPortfolio)) {
                        setTimeout(() => {
                            if (!this.positionsSection.liveStrikesUpdate) {
                                return;
                            }

                            if (this.canApplyChanges()) {
                                this.applyClicked().catch(console.error);
                            }
                        }, 500);
                    }
                }
            }
        }

        this._isWaitingAtmChangeEvent = false;
    }


    // [!] Implicitly used as event-handler in subcomponent
    @DetectMethodChanges({isAsync: true})
    async onStrategyChanged(x: CashFlowStrategy): Promise<void> {

        await this.onLongRunningOperationChanged(true);

        //hack to let loading panel render
        await delay(250);

        this.resetPricingData();
        this.resetZonesGrid();

        try {

            // CR-20260928-001: a portfolio's strategy is FIXED (ruling Q4). The strategy picker is
            // disabled whenever a portfolio is selected, so this can only fire with the
            // portfolio's own strategy; the old swap-to-the-scratchpad path is gone.
            await this.positionsSection.onStrategyChanged(x);

        } catch (e) {

            this._toastr.error('"Create Before-state positions" operation completed with errors');

        } finally {

            setTimeout(() => {
                this.onLongRunningOperationChanged(false);
            }, 1000);
        }
    }

    applyLiveAttempted$: Subject<void>;

    async tryApplyChangesLive(): Promise<void> {

        if (!this.positionsSection.liveStrikesUpdate) {
            return;
        }

        if (this.canApplyChanges()) {
            this.applyLiveAttempted$.next();
        }
    }


    @DetectMethodChanges()
    onSymbolChanged(x: TradingInstrument) {

        this.resetPricingData();
        this.resetZonesGrid();

        if (!isVoid(x)) {
            this._atmStrikeService.watch(this.settingsSection.underlying);
        }

        this.positionsSection.onSymbolChanged(x);

    }


    @DetectMethodChanges()
    async onLongRunningOperationChanged(x: boolean): Promise<void> {

        if (x) {

            this._longRunningOperationClients++;
            this.isDataLoading = true;

        } else {

            this._longRunningOperationClients--;

            if (this._longRunningOperationClients <= 0) {

                this.isDataLoading = false;

                this._longRunningOperationClients = 0;

                await this.tryApplyChangesLive();
            }
        }
    }


    @DetectMethodChanges()
    toggleCollapsed(col: ApgGridColumn) {
        let state: boolean;

        if (col === 'positions') {
            state = this.positionsColCollapsed = !this.positionsColCollapsed;
        } else if (col === 'strategies') {
            state = this.strategiesColCollapsed = !this.strategiesColCollapsed;
        } else if (col === 'pricebox') {
            state = this.priceboxColCollapsed = !this.priceboxColCollapsed;
        } else if (col === 'settings') {
            state = this.settingsColCollapsed = !this.settingsColCollapsed;
        } else if (col === 'zones-grid') {
            state = this.zonesGridColCollapsed = !this.zonesGridColCollapsed;
        }

        this.sectionCollapsed$.emit({section: col, state});
    }


    @DetectMethodChanges()
    collapseAllBut(col: ApgGridColumn) {
        if (col === 'strategies') {

            if (this.positionsColCollapsed && this.priceboxColCollapsed) {

                this.positionsColCollapsed = false;
                this.settingsColCollapsed = false;
                this.strategiesColCollapsed = false;
                this.priceboxColCollapsed = false;

            } else {
                this.positionsColCollapsed = true;
                this.settingsColCollapsed = true;
                this.strategiesColCollapsed = false;
                this.priceboxColCollapsed = true;
            }

        } else if (col === 'pricebox') {

            if (this.positionsColCollapsed && this.strategiesColCollapsed) {

                this.positionsColCollapsed = false;
                this.settingsColCollapsed = false;
                this.strategiesColCollapsed = false;
                this.priceboxColCollapsed = false;

            } else {
                this.positionsColCollapsed = true;
                this.settingsColCollapsed = true;
                this.strategiesColCollapsed = true;
                this.priceboxColCollapsed = false;
            }

        }
    }


    onQuote(quotes: QuoteDto[]) {
        const hasUpdates = this._quoteIndex.onQuote(quotes);
        this._messageBus.publishAsync({
            topic: 'Apg.QuotesUpdated',
            payload: {}
        });
    }


    toggleSettingsSectionCollapsed(): void {
        this.toggleCollapsed('settings');
    }


    canApplyChanges(): boolean {

        // O88/O92: while ORION holds the grid, Apply is dead on EVERY path - this one gate feeds
        // the button, applyClicked, force-apply and both live-strikes reprice paths.
        if (this.orionHoldsGrid) {
            return false;
        }

        const positionsHasChanges = this.positionsSection.hasChanges;

        const settingsHasChanges = this.settingsSection.hasChanges;

        const atmOutdated = this.strategiesSection
            .strategies
            .flatMap(x => x.adjustmentTypes)
            .some(x => x.isAtmStrikeOutdated);

        return positionsHasChanges || settingsHasChanges || atmOutdated;
    }


    // DO NOT REMOVE! Implicitly called
    @DetectMethodChanges({isAsync: true})
    async onSecondSpreadChanged(x: { include: boolean; strategyName: CashFlowStrategy }): Promise<void> {

        await this.onLongRunningOperationChanged(true);

        try {

            await this.positionsSection.onSecondSpreadChanged(x.include, x.strategyName);

        } catch (e) {

            this._toastr.error('"Create Before-state positions" operation completed with errors');

        } finally {

            setTimeout(() => {
                this.onLongRunningOperationChanged(false);
            }, 1000);

        }
    }


    // DO NOT REMOVE! Implicitly called
    @DetectMethodChanges({isAsync: true})
    async onSecondProtectiveOptionChanged(x: { include: boolean, strategyName: CashFlowStrategy }): Promise<void> {

        await this.onLongRunningOperationChanged(true);

        try {

            await this.positionsSection.onSecondProtectiveOptionChanged(x.include, x.strategyName);

        } catch (e) {

            this._toastr.error('"Create Before-state positions" operation completed with errors');

        } finally {

            setTimeout(() => {
                this.onLongRunningOperationChanged(false);
            }, 1000);

        }
    }


    isEmptyCell(expiration: CashFlowAdjustment): boolean {

        if (isVoid(expiration)) {
            return true;
        }

        if (expiration.isEmpty) {
            return true;
        }

        if (expiration.adjustmentTypeObj && expiration.adjustmentTypeObj.isEmpty) {
            return true;
        }

        return !isValidNumber(expiration.price);
    }


    // noinspection JSUnusedGlobalSymbols
    onPositionsRestored(args: PositionsRestoredEventArgs): void {
        this.settingsSection.onPositionsRestored(args);
    }


    // noinspection JSUnusedGlobalSymbols
    getSavePortfolioButtonText(): 'New' | 'Save' {
        // CR-20260928-001: no scratchpad to "Save" into a named portfolio any more; every
        // portfolio is created explicitly.
        return 'New';
    }


    @DetectMethodChanges({isAsync: true})
    async onPortfolioSelected(args: DxValueChanged<ApgPortfolio>): Promise<void> {

        // APG CoPilot is bound to one portfolio — reset its transient session + clear any advisory
        // highlight on a portfolio switch.
        this._copilotPreviewKey = null;
        if (this.apgCopilot) {
            this.apgCopilot.onPortfolioChanged();
        }

        this.positionsSection.overrideAtmLiveMode = null;
        this.settingsSection.globalSettings.overrideAtm = null;

        if (isVoid(args.value)) {
            console.trace('portfolio is null', args);
            this.userContext = undefined;
            this.selectedPortfolio = undefined;
            return;
        }

        if (this.userContext !== args.value.userId) {

            console.log(`[apg] user context changed: prev=${this.userContext}, curr=${args.value.userId}`);

            this.userContext = args.value.userId;

            const userObject = this.getUserObject(this.userContext);

            await this.configureServices({
                orientation: this.orientation,
                userId: userObject.userId,
                userName: userObject.userName
            });

        }

        if (!args.event) {
            this.settingsSection.globalSettings.selectedPortfolio = args.value;
            return;
        }

        await this.hedgesTotalCmp.onPortfolioSelected(args.value);

        // CR-20260928-001 (ruling Q1): remember the selection; the next boot restores it and falls
        // back to the default portfolio only when it is gone.
        this._portfoliosService.saveLastSelected(args.value);

        await this._sweetPriceTrackingService.loadPortfolioTrackingData(
            this.userContext,
            args.value.id
        );

        this.positionsSection.onPortfolioSelected(args.value);

        this.settingsSection.onPortfolioSelected(args.value);

        this.settingsSection.setDefaultTemplate(
            args.value.asset,
            args.value.strategy
        );

        if (isVoid(this.orientation)) {
            this._messageBus.publishAsync({
                topic: 'Apg.PortfolioSelected',
                payload: {
                    portfolio: args.value
                }
            });
        }

        // CR-20260904-001, Zones Z7 (contract C4). Refreshed from the host's OWN portfolio
        // selection, not from the bus message above: that message is published only when the
        // instance is not oriented, so an oriented (split) APG would never refresh its zone.
        await this.refreshCurrentZone(args.value);
    }

    /**
     * CR-20260904-001, Zones Z7. Reads the selected portfolio's current zone and caches it for
     * readCurrentZone(). Failure or no assigned template leaves it null, which the Zone view
     * renders as N/A - it never keeps the previous portfolio's number.
     */
    private onCurrentZoneChanged(zone: number): void {
        this._currentZone = isValidNumber(zone, true) ? zone : null;
        if (this.apgCopilot) {
            this.apgCopilot.refreshView();
        }
    }

    private async refreshCurrentZone(portfolio: ApgPortfolio): Promise<void> {

        this._currentZone = null;

        const portfolioKey = makePortfolioKey(portfolio);

        if (!isVoid(portfolioKey)) {
            try {
                const qry = new GetPortfolioCurrentZoneData(portfolioKey);

                const reply = await this._shellClient
                    .processQuery<GetPortfolioCurrentZoneDataReply>(qry);

                this._currentZone = isValidNumber(reply?.currentZone, true)
                    ? reply.currentZone
                    : null;

            } catch {
                this._currentZone = null;
            }
        }

        // The ORION popup content is an overlay that markForCheck does not reach, so repaint it
        // explicitly once the value has landed.
        if (this.apgCopilot) {
            this.apgCopilot.refreshView();
        }
    }


    //
    @DetectMethodChanges({isAsync: true})
    async deleteSelectedPortfolio(): Promise<void> {

        if (!this.selectedPortfolio) {
            return;
        }

        if (!this.canDeletePortfolio) {
            return;
        }

        try {
            await this.portfolioDeleteConfirmationDialog.show();
        } catch {
            return;
        }

        const userPortfolios = this.getCurrentUserPortfolios();

        const ix = userPortfolios.indexOf(this.selectedPortfolio);

        if (ix >= 0) {
            userPortfolios.splice(ix, 1);
            this._userSettingsService.removeKeysMatchingPattern(this.selectedPortfolio.id);
        }

        // hack to update portfolios data source cause it is immutable
        this.portfolios = this.portfolios.slice();

        this._portfoliosService.savePortfolios(userPortfolios);

        this._toastr.success(`[${this.selectedPortfolio!.name}] portfolio has been deleted`, 'Portfolios');

        // CR-20260928-001: fall back to the DEFAULT portfolio (flagged, else first by name).
        // `canDeletePortfolio` guarantees one is left.
        const fallback = findDefaultPortfolio(userPortfolios);

        this._selectedPortfolio = fallback;
        await this.onPortfolioSelected({value: fallback, event: 'ets'});
    }


    //
    @DetectMethodChanges()
    showPortfolioPopup() {

        this.portfolioPopup.name = null;

        this.portfolioPopup.tradingInstrument = this.settingsSection.globalSettings.tradingInstrument;

        this.portfolioPopup.strategy = this.settingsSection.globalSettings.selectedStrategy;

        this.portfolioPopup.mode = 'new';

        // The very first portfolio of a user is the default by construction.
        this.portfolioPopup.isDefault = this.getCurrentUserPortfolios().length === 0;

        this.portfolioPopup.lockPair = false;

        this.portfolioPopup.hint = null;

        this.portfolioPopup.isVisible = true;
    }

    @DetectMethodChanges()
    editPortfolio() {

        this.portfolioPopup.name = this.selectedPortfolio?.name;

        this.portfolioPopup.tradingInstrument = null;

        this.portfolioPopup.strategy = null;

        this.portfolioPopup.mode = 'edit';

        // Read the flag from the LIST entry, never from the selected instance: after a flip the
        // selector may still hold an older copy of the same portfolio.
        const listEntry = this.getCurrentUserPortfolios().find(x => x.id === this.selectedPortfolio?.id);

        this.portfolioPopup.isDefault = !!(listEntry || this.selectedPortfolio)?.isDefault;

        this.portfolioPopup.lockPair = false;

        this.portfolioPopup.hint = null;

        this.portfolioPopup.isVisible = true;
    }

    // The "Make Default Portfolio" button: one-way, the badge takes its place. Runs change
    // detection itself — the click lands inside a DevExtreme template.
    @DetectMethodChanges()
    makePortfolioDefault(): void {
        this.portfolioPopup.isDefault = true;
    }


    //
    @DetectMethodChanges({isAsync: true})
    async saveNewApgPortfolio(): Promise<void> {

        if (isVoid(this.portfolioPopup.name)) {
            this._toastr.error('Provide portfolio name');
            return;
        }

        const userPortfolios = this.getCurrentUserPortfolios();

        if (this.portfolioPopup.mode === 'new') {
            if (isVoid(this.portfolioPopup.tradingInstrument)) {
                this._toastr.error('Provide portfolio asset');
                return;
            }

            if (isVoid(this.portfolioPopup.strategy)) {
                this._toastr.error('Provide portfolio strategy');
                return;
            }

            const currentUser = this.getUserObject(this.userContext);

            const portfolio: ApgPortfolio = {
                id: getShortUUID(),
                name: this.portfolioPopup.name,
                asset: this.portfolioPopup.tradingInstrument.ticker,
                strategy: this.portfolioPopup.strategy,
                userId: currentUser.userId,
                userName: currentUser.userName,
                isDefault: false
            };

            userPortfolios.push(portfolio);

            if (this.portfolioPopup.isDefault) {
                this._portfoliosService.setDefault(userPortfolios, portfolio);
            }

            // Save BEFORE selecting: the selection handlers read the list back through the service.
            this._portfoliosService.savePortfolios(userPortfolios);

            this._selectedPortfolio = portfolio;

            await this.onPortfolioSelected({previousValue: undefined, value: this._selectedPortfolio, event: 'ets'});

            this.resolvePortfolioPopup(portfolio);

        } else {
            // Owner ruling (smoke 2026-09-28): there is ALWAYS exactly one default. The flag moves
            // by ticking another portfolio; it is never simply removed.
            const target = userPortfolios.find(x => x.id === this._selectedPortfolio.id) || this._selectedPortfolio;

            if (target.isDefault && !this.portfolioPopup.isDefault) {
                this._toastr.warning('There must always be one default portfolio. Mark another portfolio as the default instead.', 'Portfolios');
                return;
            }

            target.name = this.portfolioPopup.name;
            this._selectedPortfolio.name = this.portfolioPopup.name;

            if (this.portfolioPopup.isDefault) {
                this._portfoliosService.setDefault(userPortfolios, target);
                // Every copy of this user's portfolios the panel holds follows the flag by id.
                this.getPortfoliosByUser(target.userId).forEach(x => x.isDefault = x.id === target.id);
                this._selectedPortfolio.isDefault = true;
            }

            this._portfoliosService.savePortfolios(userPortfolios);
        }

        this.portfolioPopup.isVisible = false;

        // hack to update portfolios data source cause it is immutable
        const portfoliosCopy = this.portfolios.slice();

        setTimeout(() => {
            this.portfolios = portfoliosCopy;
            this._changeDetector.detectChanges();

            this._messageBus.publish({
                topic: 'Apg.PortfoliosUpdated',
                payload: {
                    portfolios: portfoliosCopy
                }
            });
        }, 0);
    }


    //
    @DetectMethodChanges()
    onPortfolioPopupClosed() {
        this.portfolioPopup.tradingInstrument = null;
        this.portfolioPopup.name = null;
        this.portfolioPopup.strategy = null;
        this.portfolioPopup.lockPair = false;
        this.portfolioPopup.hint = null;
        this.portfolioPopup.isVisible = false;
        // A Cancel (or a close after Save, which already resolved) ends a pending paste offer.
        this.resolvePortfolioPopup(undefined);
    }

    private resolvePortfolioPopup(portfolio: ApgPortfolio | undefined): void {
        const resolver = this._portfolioPopupResolver;
        this._portfolioPopupResolver = null;
        if (resolver) {
            resolver(portfolio);
        }
    }


    @DetectMethodChanges({isAsync: true})
    async refreshPortfolio(portfolio: ApgPortfolio) {

        let selected: 'Former' | 'Current';

        try {
            selected = await this.portfolioRefreshDialog.show(
                portfolio,
                this
            );

        } catch (e) {
            if (e) {
                this._toastr.error(e);
            }
        }

        if (isVoid(selected)) {
            return;
        }

        try {

            const selectedPortfolio = this.selectedPortfolio;
            await this.onPortfolioSelected({value: selectedPortfolio, event: 'ets'});

        } catch {

            this._toastr.error('"Refresh Portfolio" operation completed with errors');

        } finally {

            this.isDataLoading = false;

        }
    }


    // CR-20260928-001, ruling Q1: at boot select the portfolio used last; when it is gone, the
    // DEFAULT portfolio (flagged, else first by name); when the user has none, nothing.
    private async selectBootPortfolio(): Promise<void> {

        this.userContext = this._sessionService.sessionData.userId;

        const userPortfolios = this.getCurrentUserPortfolios();

        const last = this._portfoliosService.getLastSelected();

        let target = !isVoid(last)
            ? userPortfolios.find(x => x.id === last.id)
            : undefined;

        if (isVoid(target)) {
            target = findDefaultPortfolio(userPortfolios);
        }

        if (isVoid(target)) {
            return;
        }

        this._selectedPortfolio = target;

        await this.onPortfolioSelected({value: target, event: 'ets'});
    }

    // CR-20260928-001, ruling Q2: pasted positions/settings carry an underlying and a strategy. The
    // selected portfolio takes them only if its own pair matches. Otherwise the user is offered a
    // new portfolio for that pair (name pre-filled, pair locked); Cancel = nothing is pasted.
    private async ensurePortfolioFor(underlying: string, strategy: CashFlowStrategy): Promise<ApgPortfolio | undefined> {

        const selected = this.selectedPortfolio;

        if (!isVoid(selected) && selected.asset === underlying && selected.strategy === strategy) {
            return selected;
        }

        if (!this.canCreatePortfolio) {
            this._toastr.warning(
                `The pasted data is for ${underlying} / ${strategy}; a matching portfolio can only be created in your own list`,
                'Portfolios'
            );
            return undefined;
        }

        if (this._portfolioPopupResolver) {
            // A previous offer is still open; never stack two.
            return undefined;
        }

        const instrument = this._tradingInstrumentsService.getInstrumentByTicker(underlying);

        if (isVoid(instrument)) {
            this._toastr.error(`Unknown underlying "${underlying}" in the pasted data`, 'Portfolios');
            return undefined;
        }

        const selectedLabel = isVoid(selected) ? 'No portfolio is selected' : `"${selected.name}" is ${selected.asset} / ${selected.strategy}`;

        this.portfolioPopup.name = `${underlying} ${strategy}`;
        this.portfolioPopup.tradingInstrument = instrument;
        this.portfolioPopup.strategy = strategy;
        this.portfolioPopup.mode = 'new';
        this.portfolioPopup.isDefault = this.getCurrentUserPortfolios().length === 0;
        this.portfolioPopup.lockPair = true;
        this.portfolioPopup.hint = `${selectedLabel}. The pasted data is ${underlying} / ${strategy} — create a portfolio for it?`;

        const offer = new Promise<ApgPortfolio | undefined>(resolve => this._portfolioPopupResolver = resolve);

        this.portfolioPopup.isVisible = true;

        this._changeDetector.detectChanges();

        return offer;
    }


    private getCurrentUserPortfolios(): ApgPortfolio[] {
        return this.getPortfoliosByUser(this.userContext);
    }


    private getPortfoliosByUser(userId: string): ApgPortfolio[] {
        const pfs = this.portfolios.find(x => x.userId === userId);
        if (!isVoid(pfs)) {
            return pfs.items;
        }
        return [];
    }


    private getUserObject(userId: string): UserDto {

        if (this._sessionService.sessionData.userId === userId) {

            const ownUserName = makeOwnDisplayName(this._sessionService.sessionData);

            return {
                userId,
                userName: ownUserName
            };

        } else {

            return this._sessionService.sessionData.users.find(x => x.userId === userId);
        }
    }


    // noinspection JSUnusedGlobalSymbols
    async onStopTrackingRequest(): Promise<void> {
        try {

            this.isDataLoading = true;
            await this._sweetPriceTrackingService.stopTracking(
                this.userContext,
                this.selectedPortfolio
            );

        } catch (e) {
            console.error(e);
            this.isDataLoading = false;
        }
    }


    async onStartTrackingRequest(): Promise<string> {

        const beforePositions: {
            strategy: CashFlowStrategy,
            positions: BeforePositionDto[]
        }[] = this.positionsSection.getBeforePositions();

        if (isVoid(beforePositions) || beforePositions.some(pd => pd.positions.some(pos => isVoid(pos.qty)))) {
            this._toastr.error('Incorrect positions data configuration');
            return null;
        }

        let sweetPriceSettings: SweetPriceSettings;

        try {

            sweetPriceSettings = this._sweetPriceTrackingService.getSweetPriceSettings(
                this.userContext,
                this.selectedPortfolio.id
            );

            this._sweetPriceTrackingService.validateSweetPriceSettings(
                sweetPriceSettings,
                {
                    strategy: this.settingsSection.globalSettings.selectedStrategy,
                    underlying: this.settingsSection.globalSettings.underlying,
                    priceZoneAtmNotificationFilterEnabled: false
                }
            );

        } catch (e) {
            this._toastr.error(e);
            throw e;
        }

        console.assert(!isVoid(sweetPriceSettings))

        try {

            this.isDataLoading = true;

            const positions = beforePositions.map(x => x.positions);

            const hedges = await this._hedgesService.getHedgePositions(this.selectedPortfolio);

            const expirationSmartModeSettings = this._applicationSettingsService
                .adjustmentPricingGrid
                .expirationSmartMode;

            await this._sweetPriceTrackingService.startTracking(
                this.userContext,
                this.selectedPortfolio,
                positions,
                sweetPriceSettings,
                expirationSmartModeSettings,
                hedges
            );

        } catch {
            this.isDataLoading = false;
        }
    }


    async getAdjustmentPricingSettingsObject(ctx: 'Price Box' | 'Zones Grid')
        : Promise<{ errors: string[], settings: AdjustmentPricingSettingsDto[] }> {

        const errors = this.settingsSection.validate();

        if (!isVoid(errors)) {

            return {
                errors,
                settings: []
            }
        }

        const result = {
            errors: [],
            settings: []
        };

        let beforePositions: {
            strategy: CashFlowStrategy,
            positions: BeforePositionDto[]
        }[] = this.positionsSection.getBeforePositions();

        if (this.settingsSection.globalSettings.priceToOpen) {
            if (isValidNumber(this.settingsSection.globalSettings.overrideAtm, true)) {
                const poses = await this.positionsSection.getPositionsBasedOnCustomStrike(
                    this.settingsSection.globalSettings.overrideAtm
                );

                const adjustedPositions = poses.map(x => {
                    return {
                        strategy: x.strategy,
                        positions: x.positions.map(y => y.asDto()),
                    }
                });

                beforePositions = adjustedPositions;
            }
        }

        if (isVoid(beforePositions)) {
            console.error('PositionsData is empty');
            result.errors.push('Positions data is not valid');
            return result;
        }

        if (beforePositions.some(pd => pd.positions.some(pos => isVoid(pos.qty)))) {
            result.errors.push('Positions data is not valid');
            console.error('Positions Data has bad qty');
            return result;
        }

        const strategySettings: ICashFlowAdjustmentSettingsTemplate
            = this.settingsSection.getSettings();

        if (isVoid(strategySettings)) {
            result.errors.push('Strategy settings are Not Valid');
            return result;
        }

        const template = this.settingsSection.globalSettings.selectedTemplate;
        let templateId = 'N/A';
        let templateName = 'N/A';

        if (!isVoid(template)) {
            templateId = template.templateId;
            templateName = template.templateName;
        }

        const adjustmentPricingSettings: AdjustmentPricingSettingsDto[]
            = strategySettings.settings.map(s => {

            const beforeState = beforePositions
                .find(x => x.strategy === s.strategyName)?.positions;

            if (isVoid(beforeState)) {
                console.error(`Cannot Determine Before State for '${s.strategyName}' strategy`)
                return null;
            }

            const theoTarget = strategySettings.globalSettings.theoreticalPriceTarget;
            const theoMode = strategySettings.globalSettings.theoreticalPriceMode;

            let useTheo = false;

            if (!isVoid(theoTarget) && !isVoid(theoMode) && theoMode === 'All Theoretical') {

                if (theoTarget === 'All') {
                    useTheo = true;
                } else if (theoTarget === 'Price Box') {
                    useTheo = ctx === 'Price Box';
                } else if (theoTarget === 'Zones Grid') {
                    useTheo = ctx === 'Zones Grid';
                }

                if (useTheo) {
                    const iv = this.settingsSection.globalSettings.theoreticalPriceIv || 0;
                    beforeState.forEach(p => p.ticker += ` !${iv}`);
                } else {
                    beforeState
                        .filter(p => p.ticker.endsWith(' !'))
                        .forEach(p => {
                            const ix = p.ticker.indexOf(' !');
                            const ticker = p.ticker.substring(0, ix);
                            p.ticker = ticker;
                        });
                    strategySettings.globalSettings.theoreticalPriceMode = null;
                    strategySettings.globalSettings.theoreticalPriceIv = null;
                }
            }


            const dto: AdjustmentPricingSettingsDto = {
                templateId,
                templateName,

                strategy: s.strategyName,
                underlying: this.settingsSection.globalSettings.underlying,
                comboId: null,
                priceToDestination: strategySettings.globalSettings.priceToDestination,
                priceToOpen: strategySettings.globalSettings.priceToOpen,
                addCurrentExpiration: strategySettings.globalSettings.addCurrentExpiration,
                beforeState,
                isStrategyAdvancedMode: this.settingsSection.globalSettings.isStrategyAdvancedMode,
                isDynamicOffsets: strategySettings.globalSettings.isDynamicOffsets,
                theoreticalPriceIv: strategySettings.globalSettings.theoreticalPriceIv,
                theoreticalPriceMode: strategySettings.globalSettings.theoreticalPriceMode,
                theoreticalPriceIvList: '',

                // spread
                spreadOffset: s.strategySettings.spreadOffset,
                spreadWidth: s.strategySettings.spreadWidth,
                spreadRollXDaysBeforeExpiration: s.strategySettings.spreadRollXDaysBeforeExpiration,
                spreadRollToDaysToExp: s.strategySettings.spreadRollToDaysToExp,
                spreadOverrideRollToDaysToExp: s.strategySettings.spreadOverrideRollToDaysToExp,
                spreadEvergreenOverrideRollToDaysToExp: s.strategySettings.spreadEvergreenOverrideRollToDaysToExp,

                // second spread
                secondSpreadOffset: s.strategySettings.secondSpreadOffset,
                secondSpreadWidth: s.strategySettings.secondSpreadWidth,
                secondSpreadRollXDaysBeforeExpiration: s.strategySettings.secondSpreadRollXDaysBeforeExpiration,
                secondSpreadRollToDaysToExp: s.strategySettings.secondSpreadRollToDaysToExp,
                secondSpreadOverrideRollToDaysToExp: s.strategySettings.secondSpreadOverrideRollToDaysToExp,
                secondSpreadEvergreenOverrideRollToDaysToExp: s.strategySettings.secondSpreadEvergreenOverrideRollToDaysToExp,

                // protective option
                protectiveOptionOffset: s.strategySettings.protectiveOptionOffset,
                protectiveOptionRollXDaysBeforeExpiration: s.strategySettings.protectiveOptionRollXDaysBeforeExpiration,
                protectiveOptionRollToDaysToExp: s.strategySettings.protectiveOptionRollToDaysToExp,
                protectiveOptionOverrideRollToDaysToExp: s.strategySettings.protectiveOptionOverrideRollToDaysToExp,
                protectiveOptionEvergreenOverrideRollToDaysToExp: s.strategySettings.protectiveOptionEvergreenOverrideRollToDaysToExp,
                protectiveOptionRollToXBusinessDaysToExp: s.strategySettings.protectiveOptionRollToXBusinessDaysToExp,

                // 2nd protective option
                secondProtectiveOptionOffset: s.strategySettings.secondProtectiveOptionOffset,
                secondProtectiveOptionRollXDaysBeforeExpiration: s.strategySettings.secondProtectiveOptionRollXDaysBeforeExpiration,
                secondProtectiveOptionRollToDaysToExp: s.strategySettings.secondProtectiveOptionRollToDaysToExp,
                secondProtectiveOptionOverrideRollToDaysToExp: s.strategySettings.secondProtectiveOptionOverrideRollToDaysToExp,
                secondProtectiveOptionEvergreenOverrideRollToDaysToExp: s.strategySettings.secondProtectiveOptionEvergreenOverrideRollToDaysToExp,
                secondProtectiveOptionRollToXBusinessDaysToExp: s.strategySettings.secondProtectiveOptionRollToXBusinessDaysToExp,

                //
                customDates: strategySettings.expirationSettings.customDates.map(x => x.value),
                joinRollBuffer: strategySettings.expirationSettings.joinRollBuffer,
                expirationsToLookForward: strategySettings.expirationSettings.expirationsToLookForward,
                expirationsToLookForwardAbove: strategySettings.expirationSettings.expirationsToLookForward,
                expirationsToLookForwardDown: strategySettings.expirationSettings.expirationsToLookForward,

                //
                expirationSmartModeSettings: this._applicationSettingsService.adjustmentPricingGrid.expirationSmartMode,
                useMarginEfficientAdjustment: this._applicationSettingsService.adjustmentPricingGrid.useMarginEfficientAdjustment,
                adjustToQty: strategySettings.globalSettings.adjustToQty,

                //
                overrideAtm: strategySettings.globalSettings.overrideAtm,

                //
                useCharlesSchwabFix: this._applicationSettingsService.adjustmentPricingGrid.useCharlesSchwabFix
            };

            const removeBrackets = function(str: string) {
                return str.replace(/\s*\([^)]*\)/g, '');
            }

            // adjustments filter
            const json = this._visibleOffsetsService.priceBox.favoriteAdjustments;
            const favs = JSON.parse(json) as FavoriteAdjustment[] || [];
            const filter = favs
                .map(x => {
                    const one = removeBrackets(x.oneAdjustment);
                    const two = removeBrackets(x.twoAdjustment);
                    return [one, two];
                })
                .map(x => x.join('+'));
            dto.adjustmentsFilter = filter;

            // CR-20260927-003: the zone-drift one-shot prices #2B only - the favourites are
            // REPLACED for this request, never written back (the ORION pattern). The couple shape
            // routes a Calls & Puts run to the two-strategy filtered path; a single id routes a
            // one-strategy run to its filtered path.
            if (ctx === 'Price Box' && this._zoneDriftOneShot) {
                dto.adjustmentsFilter = strategySettings.settings.length >= 2 ? ['2b+2b'] : ['2b'];
                console.log(`[apg][zone-drift] request: strategy=${dto.strategy} filter=[${dto.adjustmentsFilter}] overrideAtm=${dto.overrideAtm}`);
            }
            return dto;
        });

        if (adjustmentPricingSettings.length !== strategySettings.settings.length) {
            result.errors.push('Strategies count does not match, unknown error');
            return result;
        }


        result.settings = adjustmentPricingSettings;
        return result;
    }


    // noinspection JSUnusedGlobalSymbols
    onSweetPriceSettingsRequest(x: SweetPricePopupConfig): void {
        const tpl = this.settingsSection.globalSettings.selectedTemplate;
        if (isVoid(tpl)) {
            throw Error('Template not selected');
        }
        x.daysToLookForward = tpl.expirationSettings.expirationsToLookForward;
        x.userId = this.userContext;
        x.portfolio = this.selectedPortfolio;
        x.template = tpl;
    }


    @DetectMethodChanges({isAsync: true})
    async onSweetPriceSyncRequest(): Promise<void> {
        const result = await this.syncTrackingDialog.show(
            this.userContext,
            this.selectedPortfolio.id,
            this.positionsSection.positionsData.map(x => x.positions.map(y => y.asDto())),
            this.settingsSection.globalSettings.selectedTemplate
        );

        if (result === 'Current') {

            try {

                await this.onStartTrackingRequest();
                this._toastr.info('Sweet Price Tracking was updated to track new position configuration');

            } catch (e) {

                //
                this._toastr.error('"Update Sweet Price Tracking" operation completed with errors');

            }

        } else {

            //TODO:

        }
    }


    // noinspection JSUnusedGlobalSymbols
    sweetPriceIsSync(): boolean {

        const userId = this.userContext;

        const portfolioId = this.selectedPortfolio.id;

        const positions = this.positionsSection.positionsData.map(x => x.positions.map(y => y.asDto()));

        const templateId = this.settingsSection.globalSettings.selectedTemplate
            ? this.settingsSection.globalSettings.selectedTemplate.templateId
            : null;

        return this._sweetPriceTrackingService.isSync(
            userId,
            portfolioId,
            positions,
            templateId
        );
    }


    // noinspection JSUnusedGlobalSymbols
    sweetPriceIsTracking(): boolean {
        const userId = this.userContext;
        const portfolioId = this.selectedPortfolio.id;
        return this._sweetPriceTrackingService.isTracking(userId, portfolioId);
    }


    // noinspection JSUnusedGlobalSymbols
    getContextUserId(): string {
        return this.userContext;
    }


    // noinspection JSUnusedGlobalSymbols
    async onTemplateUpdated(tpl: ICashFlowAdjustmentSettingsTemplate): Promise<void> {

        if (isVoid((tpl))) {
            this._toastr.error('"Update Template" operation completed with errors');
            console.error('template is null');
            return;
        }

        this.isDataLoading = true;

        try {
            const cmd = new UpdateTrackingObjectsOnTemplateUpdate(
                this.userContext,
                tpl
            );
            await this._shellClient.processCommand(cmd);
        } catch {
            this.isDataLoading = false;
        }
    }


    // noinspection JSUnusedGlobalSymbols
    async onSweetPriceSettingsUpdated(): Promise<void> {

        const settings = this._sweetPriceTrackingService.getSweetPriceSettings(
            this.userContext,
            this.selectedPortfolio.id
        );

        try {
            this._sweetPriceTrackingService.validateSweetPriceSettings(
                settings,
                {
                    strategy: this.settingsSection.globalSettings.selectedStrategy,
                    underlying: this.settingsSection.globalSettings.underlying,
                    priceZoneAtmNotificationFilterEnabled: false
                },
            );
        } catch (e) {
            this._toastr.error(e);
            return;
        }

        this.isDataLoading = true;

        try {

            const cmd = new UpdateTrackingObjectOnSweetPriceSettingsUpdate(
                this.userContext,
                this.selectedPortfolio,
                settings
            );

            await this._shellClient.processCommand(cmd);
        } catch {
            this.isDataLoading = false;
        }
    }

    // noinspection JSUnusedGlobalSymbols
    @DetectMethodChanges()
    onTemplateApplied() {

        // This is part of the copy/paste settings functionality
        // when pasting to an empty panel. It is split into 2 steps:
        //  1. Applying default portfolio and template
        //  2. After template applied, the object with the following key has to be in clipboard
        // If no such key means that this call is not part of copy/paste
        const templateSettings = this._clipboardService.get('apg.copy/paste.settings.temp');

        if (!isVoid(templateSettings)) {
            this.settingsSection.applyPasteTemplate(templateSettings);
        }

        //

        this.positionsSection.syncPositionsAndSettingsRegardingSecondOptions();

        // CR-20260927-003: the portfolio's template is applied and its positions are built - the
        // moment a zone-drift open has been waiting for.
        this.zoneDriftConditionMet('template', 'template applied');
    }


    // noinspection JSUnusedGlobalSymbols
    getSecondOptionsConfiguration() {

        const config = this.positionsSection.positionsData.map(pd => {

            const hasSecondSpread = pd.positions.some(x => x.role.startsWith('SecondSpread'));

            const hasSecondProtectiveOption = pd.positions.some(x => x.role.startsWith('SecondProtective'));

            return {
                hasSecondSpread,
                hasSecondProtectiveOption
            };
        });

        return config;
    }


    // noinspection JSUnusedGlobalSymbols
    isDynamicOffsetEnabled(): boolean {
        return this.settingsSection.globalSettings.isDynamicOffsets;
    }


    // noinspection JSUnusedGlobalSymbols
    getSelectedPortfolio(): ApgPortfolio {
        return this.selectedPortfolio;
    }


    // noinspection JSUnusedGlobalSymbols
    getSelectedTemplate(): ICashFlowAdjustmentSettingsTemplate {
        return this.settingsSection.globalSettings.selectedTemplate;
    }


    // noinspection JSUnusedGlobalSymbols
    getDefaultTemplate(underlying: string, strategy: CashFlowStrategy): ICashFlowAdjustmentSettingsTemplate {
        return this._cashFlowStrategyTemplatesService
            .getOrCreateDefaultTemplate(underlying, strategy);
    }


    // noinspection JSUnusedGlobalSymbols
    isOperationInProgress(): boolean {
        return this._longRunningOperationClients > 0;
    }


    async pastePositions(positionsData: PositionsData[]): Promise<void> {

        const underlying = positionsData[0].positions[0].underlyingSymbol;

        let strategy: CashFlowStrategy = positionsData.length === 2
            ? 'Calls & Puts'
            : positionsData[0].positions[0].strategy;

        // CR-20260928-001: a portfolio's pair is FIXED. The paste lands on the selected portfolio
        // only when the pair matches; otherwise the user is offered a new portfolio (ruling Q2).
        // The old code silently re-pointed a NAMED portfolio's asset/strategy here — gone.
        const portfolio = await this.ensurePortfolioFor(underlying, strategy);

        if (isVoid(portfolio)) {
            return;
        }

        this._savedPositionsService.save(
            portfolio.id,
            underlying,
            strategy,
            positionsData
        );

        this._selectedPortfolio = portfolio;
        await this.onPortfolioSelected({value: portfolio, event: 'ets'});
    }


    async pasteSettings(pasteSettings: ICashFlowAdjustmentSettingsTemplate,
                        isPartOfCombo?: boolean): Promise<void> {

        if (!isPartOfCombo) {
            const portfolio = await this.ensurePortfolioFor(
                pasteSettings.underlying,
                pasteSettings.strategyName
            );

            if (isVoid(portfolio)) {
                return;
            }

            // this should be picked up later in 'onTemplatedApplied()' method
            this._clipboardService.put('apg.copy/paste.settings.temp', pasteSettings);

            this._selectedPortfolio = portfolio;
            await this.onPortfolioSelected({value: portfolio, event: 'ets'});
            return;
        }

        // Part of a combo: the combo handler already settled the portfolio.
        this._clipboardService.put('apg.copy/paste.settings.temp', pasteSettings);
    }


    // noinspection JSUnusedGlobalSymbols
    async copyPositionsAndSettings(): Promise<void> {
        if (!this.selectedPortfolio) {
            return;
        }

        const positionsData = this.positionsSection.positionsData;

        if (isVoid(positionsData)) {
            this._toastr.error('No positions found');
            return;
        }

        const positionsDtos = positionsData.map(pd => pd.positions.map(p => p.asDto()));

        const settings = this.settingsSection.getSettings();

        const obj = {
            underlying: settings.underlying,
            strategy: settings.strategyName,
            positions: positionsDtos,
            settings
        };

        const json = JSON.stringify(obj);

        const key = 'apg.copy/paste.combination';

        const data = key + json;

        await navigator.clipboard.writeText(data);

    }


    // noinspection JSUnusedGlobalSymbols
    async pastePositionsAndSettings(): Promise<void> {

        const clipboardData = await navigator.clipboard.readText();

        if (!clipboardData.startsWith('apg.copy/paste.combination')) {
            this._toastr.error('Cannot paste positions and settings: clipboard contains corrupted data');
            return Promise.resolve();
        }

        const positionsAndSettingsJson = clipboardData.split('apg.copy/paste.combination')[1];

        const parsedObject = JSON.parse(positionsAndSettingsJson) as {
            underlying: string,
            strategy: CashFlowStrategy
            positions: BeforePositionDto[][],
            settings: ICashFlowAdjustmentSettingsTemplate
        };

        //
        // Prepare positions
        //
        const positionDtos = parsedObject.positions;

        if (isVoid(positionDtos)) {
            this._toastr.error('Positions Data corrupted, cannot proceed');
            console.error(positionDtos);
            return;
        }

        const chain = await this._positionsStateService.getOptionChain(parsedObject.underlying);

        if (isVoid(chain)) {
            this._toastr.error(`Failed to retrieve option chain for ${parsedObject.underlying}`);
            return;
        }

        const models = positionDtos.map(positions => {
            const model = this._positionsStateService
                .makeBeforePositionModelFromDto(positions, chain);
            return model;
        });

        const positionsData = models.map(m => {
            return {
                positions: m,
                strategy: m[0].strategy,
                isSavedState: true
            } as PositionsData;
        });

        //
        // Prepare Settings
        //
        const pasteSettings = parsedObject.settings;

        if (isVoid(pasteSettings)) {
            this._toastr.error('Settings data is corrupted');
            console.error(pasteSettings);
            return;
        }

        await this.onLongRunningOperationChanged(true);

        try {
            // CR-20260928-001 (ruling Q2): settle the target portfolio ONCE for the whole combo.
            const portfolio = await this.ensurePortfolioFor(pasteSettings.underlying, pasteSettings.strategyName);

            if (isVoid(portfolio)) {
                return;
            }

            await this._cashFlowStrategyTemplatesService
                .clearLastUsedTemplate(portfolio.id);

            await this.pasteSettings(pasteSettings, true);

            await this.pastePositions(positionsData);

        } finally {

            await this.onLongRunningOperationChanged(false);

        }
    }


    getPositions(): PositionsData[] {
        return this.positionsSection.positionsData;
    }


    private async onDataLoaded(reply: GetAdjustmentSolutionsReply) {
        try {

            if (isVoid(reply)) {
                return;
            }

            if (isVoid(reply.requestId)) {
                return;
            }

            const ix = this._pendingRequestId.indexOf(reply.requestId);

            if (ix < 0) {
                return;
            }

            this._pendingRequestId.splice(ix, 1);

            // ⚠ THERE IS NO APG CoPILOT FORK HERE ANY MORE (CR-20260910-001 milestone 1). A run used
            // to arrive through this method: it fired `GetAdjustmentSolutions` under a 'copilot-'
            // request id, and this fork resolved the awaiting promise with the freshly-priced cells.
            // ORION now sends its own command and receives its own delivery, and the Price Box is
            // populated FROM that delivery through `populatePriceBoxFromOrion`, which calls
            // `onDataLoadedInternally` directly. This method is back to serving Apply, Zones Grid and
            // Pricing Steps only.

            if (reply.error) {
                this._toastr.error('"Calculate Adjustments" operation completed with errors');
                return;
            }

            // CR-20260918-003: the backend's warnings (e.g. a before-state leg not listed on the
            // replay day, so nothing that closes or rolls it can price) reach the plain Apply /
            // Zones Grid / Pricing Steps runs as toasts. The ORION list shows its own.
            (reply.warnings || []).filter(w => !isVoid(w)).forEach(w => this._toastr.warning(w, 'Adjustments'));

            if (reply.adjustments.length === 0) {
                this._toastr.error('Service returned no result');
                return;
            }

            await this.onDataLoadedInternally(reply);

        } finally {
            if (this._pendingRequestId.length === 0) {
                this.isDataLoading = false;

                if (this._sessionService.loginResult.isQuoteGeneratorShell) {
                    setTimeout(() => {
                        this._messageBus.publish({
                            topic: 'Qg.RefreshLastQuoteRequest',
                            payload: {
                                ticker: this.getSelectedTemplate()?.underlying
                            }
                        });
                    }, 1000);
                }
            }
        }
    }

    private async onDataLoadedInternally(reply: GetAdjustmentSolutionsReply) {

        const isZonesGridData = reply.requestId.startsWith('zones');
        const isPricingStepsData = reply.requestId.startsWith('pricingSteps');

        const chain = await this._optionChainsService.getChain(this.settingsSection.underlying);

        // Guard: getChain can resolve null (underlying cleared/changed or chain evicted mid-calc). Skip
        // the days-to-expiration enrichment in that case but STILL fall through to setData below (a
        // throw here would also leak a CoPilot run's promise).
        const expirationDates = (!isVoid(chain) && !isVoid(chain.expirations))
            ? chain.expirations
                .map(x => x.dateWithDaysToExpiration.split(' '))
                .filter(x => x.length == 3)
                .map(x => x[1] + ' ' + x[2])
            : [];

        reply.adjustments.forEach(adj => {

            const dateWithExp = expirationDates.find(x => x.startsWith(adj.expirationPretty));

            if (!isVoid(dateWithExp)) {
                adj.expirationDatePrettyWithDaysToExpiration = dateWithExp;
            }

            this.setRatios(adj);

        });

        if (isPricingStepsData) {
            if (this.apgPricingStepsPopup?.isVisible) {
                this._strategyCodeIndex.addDataPricingSteps(reply.adjustments);
                this.apgPricingStepsPopup.setData(reply.adjustments);
            } else {
                this.resetPricingStepsSubscriptions();
            }
            return;
        }

        if (isZonesGridData) {
            this._quoteIndex.addDataZones(reply.adjustments);
            this._strategyCodeIndex.addDataZones(reply.adjustments);
        } else {
            this._quoteIndex.addData(reply.adjustments);
            this._strategyCodeIndex.addData(reply.adjustments)
        }

        if (isZonesGridData) {
            if (this.zonesGridSection) {
                this.zonesGridSection.setData(reply.adjustments)
            }
        } else {
            this.extractExtras(reply.adjustments);
            this.strategiesSection.setData(reply.adjustments);
            this.priceboxSection.setData(reply.adjustments);
            // An ORION reply holds the adjustment the BACKEND chose by the zone; the saved favourites
            // must not hide that row. Cleared by the next Apply (any non-ORION reply).
            // CR-20260927-003: the zone-drift one-shot's #2B rows are rule-chosen too.
            // The tag list lives in BACKEND_SELECTED_REQUEST_ID_PREFIXES (top of file).
            this.priceboxSection.rowsSelectedByBackend = isBackendSelectedRequestId(reply.requestId);
            this.positionsSection.updateAtmDistance();

            // CR-20260904-001 (Filtering rules, F3): the columns have just changed, so refresh the
            // landmine verdicts for the span they cover and publish them. NOT awaited - the Price Box
            // must paint at its normal speed (contract C1 G8), and this is also the PREFETCH that makes
            // BR-K1 true: by the time the trader presses Run in ORION the answer is already in hand.
            this.ensureLandmineDaysForColumns();
        }

        const theoAdjustment = reply.adjustments
            .find(adj => !isVoid(adj.optionStrategyCode) && adj.optionStrategyCode.indexOf(' !') > 0);

        if (!isVoid(theoAdjustment)) {

            if (isZonesGridData) {
                this._zonesGridSectionHeaderVolatility = '(T)';
            } else {
                this._priceboxSectionHeaderVolatility = '(T)';
            }

            const parts = theoAdjustment.mainLegs[0].ticker.split(' ');
            const volPart = parts.find(p => p.startsWith('!'));

            if (!isVoid(volPart)) {
                const volString = volPart.substring(1);
                const number = parseFloat(volString);
                if (isValidNumber(number, true)) {

                    if (isZonesGridData) {
                        this._zonesGridSectionHeaderVolatility = `(T)`;
                    } else {
                        this._priceboxSectionHeaderVolatility = `(T)`;
                    }
                }
            }
        } else {
            if (isZonesGridData) {
                this._zonesGridSectionHeaderVolatility = null;
            } else {
                this._priceboxSectionHeaderVolatility = null;
            }
        }


        // in case of missing quotes, fill out from cache
        setTimeout(() => {
            if (!this.settingsSection.globalSettings.useTheoreticalPrices) {
                // we avoid populating strategy prices from cache if its theo prices
                // because IV probably has changed, and previous values don't make sense
                const strategyPrices = this._lastQuoteCache.getAllLastStrategyPrices();
                this.onStrategyPrice(strategyPrices);
            }

            const allQuotes = this._lastQuoteCache.getAllQuotes();
            this.onQuote(allQuotes);
        }, 50);

        if (this.settingsSection.globalSettings.useTheoreticalPrices) {
            if (this.settingsSection.globalSettings.theoreticalPriceMode === 'Fill Blanks') {
                if (isZonesGridData) {
                    if (this.settingsSection.globalSettings.theoreticalPriceTarget !== 'Price Box') {
                        this._toastr.info('"Fill Blanks" mode is selected for "Zones Grid". ' +
                            'Blank adjustments examination will start in 10 seconds',
                            'Theoretical Prices - "Fill Blanks"',
                            {timeOut: 10 * 1000});
                    }
                } else {
                    if (this.settingsSection.globalSettings.theoreticalPriceTarget !== 'Zones Grid') {
                        this._toastr.info('"Fill Blanks" mode is selected for "Price Box". ' +
                            'Blank adjustments examination will start in 10 seconds',
                            'Theoretical Prices - "Fill Blanks"',
                            {timeOut: 10 * 1000});
                    }
                }

                setTimeout(() => this
                    .fillTheBlanks(reply.adjustments, isZonesGridData), 10 * 1000);
            }
        }
    }

    private onTrackingStartedConfirmation(payload: TrackingStartedMessage) {

        this._sweetPriceTrackingService.onTrackingStartedConfirmation(payload);

        if (isVoid(this.selectedPortfolio)) {
            return;
        }

        const key = this.userContext + this.selectedPortfolio.id;

        if (payload.id !== key) {
            return;
        }

        this.isDataLoading = false;
    }

    private onTrackingStoppedConfirmation(payload: TrackingStoppedMessage) {

        this._sweetPriceTrackingService.onTrackingStoppedConfirmation(payload);

        if (isVoid(this.selectedPortfolio)) {
            return;
        }

        const key = this.userContext + this.selectedPortfolio.id;

        if (payload.id !== key) {
            return;
        }

        this.isDataLoading = false;
    }

    private onTrackingRequestError(payload: TrackingRequestErrorMessage) {

        if (isVoid(this.selectedPortfolio)) {
            return;
        }

        if (isVoid(this.settingsSection.globalSettings.selectedTemplate)) {
            return;
        }

        if (payload.origin === 'portfolio') {
            const key = this.userContext + this.selectedPortfolio.id;

            if (payload.requestId !== key) {
                return;
            }

            this.isDataLoading = false;

            this._toastr.error(payload.message);

        } else if (payload.origin === 'template') {

            const key = this.settingsSection.globalSettings.selectedTemplate.templateId;

            if (payload.requestId !== key) {
                return;
            }

            this.isDataLoading = false;

            this._toastr.error(payload.message);
        }
    }

    private onTrackingTemplateUpdated(payload: TrackingTemplateUpdated) {
        this.isDataLoading = false;

        if (isVoid(payload.message)) {
            return;
        }

        this._toastr.success(payload.message);
    }

    private onTrackingNotificationSettingsUpdated(payload: TrackingNotificationSettingsUpdated) {
        this.isDataLoading = false;

        if (isVoid(payload.message)) {
            return;
        }
        this._toastr.success(payload.message);
    }

    showPriceBoxMenu() {
        this.priceBoxMenu.isPricingStepsAvailable = this.isPricingStepsAvailable;
        this.priceBoxMenu.show();
    }

    showZonesGriMenu() {
        this.zonesGridMenu.show();
    }

    private setRatios(adj: CashFlowAdjustment) {
        adj.perContract = 1;
        adj.ratio = 1;

        if (adj.isEmpty) {
            return;
        }

        const a = adj.beforeState.find(x => x.role === 'ShortOption');
        const b = adj.afterState.find(x => x.role === 'ShortOption');


        if (isVoid(a) || isVoid(b)) {
            return;
        }

        if (isVoid(a.ticker) || isVoid(b.ticker)) {
            return;
        }

        if (b.qty === a.qty) {
            return;
        }

        adj.perContract = Math.max(Math.abs(b.qty), Math.abs(a.qty));

        const qtties = adj.mainLegs
            .concat(adj.linkedLegs)
            .concat(adj.secondLinkedLegs)
            .concat(adj.thirdLinkedLegs)
            .map(x => x.qty);

        const ratio = findHCF(qtties);

        adj.ratio = ratio;
    }

    private extractExtras(pricings: CashFlowAdjustment[]) {

        pricings.forEach(pricing => {

            if (pricing.isPriceToOpen) {
                return;
            }

            try {

                const mappings = pricing.afterState.map((el, i) => ({
                    role: el.role,
                    pair: [el, pricing.beforeState[i]]
                }));

                const so = mappings.find(x => x.role === 'ShortOption');

                if (isVoid(so)) {
                    return;
                }

                const diff = Math.abs(so.pair[0].qty) - Math.abs(so.pair[1].qty);

                if (diff === 0) {
                    return;
                }

                const isIncrease = diff > 0;

                const extraPositions = mappings.map(m => {

                    const afterPosition = m.pair[isIncrease ? 0 : 1];
                    const beforePosition = m.pair[isIncrease ? 1 : 0]

                    const ticker = afterPosition.ticker;
                    const qty = afterPosition.qty - beforePosition.qty;
                    let role = afterPosition.role;
                    let expiration = afterPosition.expiration;
                    let price = null;
                    let side = isIncrease ? afterPosition.side : beforePosition.side;
                    let strategy = afterPosition.strategy;
                    let underlying = afterPosition.underlying;
                    let type = afterPosition.type;
                    let expirationFull = afterPosition.expirationFull;
                    let strike = afterPosition.strike;

                    const pos: SolutionPositionDto = {
                        ticker,
                        qty,
                        role,
                        expiration,
                        price,
                        side,
                        strategy,
                        underlying,
                        type,
                        expirationFull,
                        strike
                    };

                    return pos;
                });

                pricing.extraPositions = extraPositions;
                pricing.extraType = isIncrease ? 'increase' : 'decrease';

                this._quoteIndex.addToQuoteIndex(extraPositions, isIncrease);

            } catch {
                //
            }
        });
    }

    getAdjustmentPrice(x: CashFlowAdjustment): number {
        const px = (!isValidNumber(x.price) ? NaN : (x.price / x.perContract)) * x.ratio;
        return px;
    }

    private async generateAdjustmentOrders(args: { memento: DefaultsMemento, generateOrders?: boolean }) {

        if (!args.generateOrders) {

            const promises = this.positionsSection.positionsData.map(async (x, ix) => {
                const mm = ix === 0 ? args.memento.first : args.memento.second;
                const afterState = await this.calculateAfterState(x.positions, mm);
                return afterState;
            });

            const resolved = await Promise.all(promises);

            this.positionsSection.applyPositionsAfterStateAfterDefaultsUpdate(resolved);

            return;
        }

        const beforeState = this.positionsSection.positionsData;

        const promises = beforeState.map(async (pd, ix) => {

            const mm = ix == 0 ? args.memento.first : args.memento.second;
            const cfa = await this.makeCashflowAdjustmentObject(pd, mm);
            return cfa;

        });

        const promiseResult = await Promise.all(promises);

        const pricings = promiseResult.filter(x => !isVoid(x));

        if (pricings.length === 0) {
            this._toastr.info("There was no difference between " +
                "before and after state, hence no orders were generated")
            return;
        }


        let strategyType: 'single' | 'double' = pricings.length == 2 ? 'double' : 'single';

        this.solutionPopupCmp.show(
            this.selectedPortfolio,
            pricings,
            strategyType,
            undefined
        );
    }

    private async makeCashflowAdjustmentObject(x: PositionsData, memento: DefaultSettingsMemento): Promise<CashFlowAdjustment> {

        const beforeState = x.positions.map(x => {
            const p: SolutionPositionDto = {
                ticker: x.getTicker(),
                side: x.side,
                qty: x.qty,
                underlying: x.underlyingSymbol,
                type: x.type,
                strike: x.strike,
                expiration: makeGuiFriendlyExpirationDate(x.selectedExpiration.optionExpirationDate),
                role: x.role,
                strategy: x.strategy,
                priceMultiplier: Math.abs(x.qty) * -1,
                expirationFull: makeFullExpirationDate(x.selectedExpiration.optionExpirationDate)
            };

            return p;
        });
        const afterState = await this.calculateAfterState(x.positions, memento);

        this.markChangedPositions(beforeState, afterState);

        await this._afterStateDetalizationService.add(afterState, x.strategy);

        const orders = this.makeOrdersForPositionAdjustment(x.positions, afterState);

        if (orders.length === 0) {
            return null;
        }

        const mainLegs = [];
        const linkedLegs = [];
        const secondLinkedLegs = [];

        orders.forEach((o, ix) => {
            if (ix < 4) {
                mainLegs.push(o);
            } else if (ix < 8) {
                linkedLegs.push(o);
            } else {
                secondLinkedLegs.push(o);
            }
        });

        const shortOption = x.positions.find(x => x.role === 'ShortOption');

        this._quoteIndex.addToQuoteIndex(beforeState, false);
        this._quoteIndex.addToQuoteIndex(afterState, true);
        this._quoteIndex.addToQuoteIndex(orders, false);

        const optionStrategyCode = orders.map(x => `${x.qty}|${x.ticker}`).join(';');

        this._lastQuoteCache.subscribeStrategyCodesDiff([], [optionStrategyCode]);

        const dayOfWeek = DateTime.fromFormat(shortOption.selectedExpiration.optionExpirationDate
            , 'yyyy-MM-dd').weekdayShort;

        balanceStatePositions(beforeState, afterState);

        const obj: CashFlowAdjustment = {
            adjustmentTypeObj: undefined,
            logicalIdWithStrategy: "",
            uniqueAdjustmentSpecificationId: getShortUUID(),
            cashFlowContext: "N/A",
            cashFlowStrategy: x.strategy,
            underlying: x.positions[0].underlyingSymbol,
            logicalId: '#0',
            initialStrike: shortOption.strike + '',
            destinationStrike: shortOption.strike + '',
            direction: 'Sideways',
            adjustmentType: 'CUSTOM',
            isEmpty: false,
            optionStrategyCode: optionStrategyCode,
            expiration: shortOption.selectedExpiration.optionExpirationDate,
            expirationPretty: makeGuiFriendlyExpirationDate(shortOption.selectedExpiration.optionExpirationDate),
            mainLegs: mainLegs,
            linkedLegs: linkedLegs,
            secondLinkedLegs: secondLinkedLegs,
            thirdLinkedLegs: [],
            beforeState: beforeState,
            afterState: afterState,
            price: null,
            marketDelta: null,
            isDefault: false,
            description: "Sideways",
            atmStrike: shortOption.strike,
            coupleTag: "99+99",
            dayOfWeek: dayOfWeek,
            templateName: this.getSelectedTemplate().templateName,
            // "templateId": this.getSelectedTemplate().templateId,
            dynamicOffsets: false,
            actualSpreadOffset: null,
            actualSpreadWidth: null,
            actualSecondSpreadWidth: null,
            actualProtectiveOptionOffset: null,
            actualSecondProtectiveOptionOffset: null,
            actualSecondSpreadOffset: null,
            isPriceToOpen: false,
            expirationDatePrettyWithDaysToExpiration: shortOption.selectedExpiration.dateWithDaysToExpiration,
            perContract: null,
            ratio: null,
            extraPositions: [],
            extraType: null,
            positionsVisible: true,
            isPriceToClose: false
        };

        return obj;
    }

    private async calculateAfterState(beforeState: BeforePositionModel[], memento: DefaultSettingsMemento)
        : Promise<SolutionPositionDto[]> {

        const result = [];

        const desiredState: Record<CashFlowStrategyRole, DesiredMementoState> = {} as any;

        const defaults = this._positionsStateService.getDefaultsProvider(
            this.selectedPortfolio.id,
            this.settingsSection.globalSettings.underlying,
            'double'
        );

        // Short Option
        const shortOption = beforeState.find(x => x.role === 'ShortOption');

        const baseQtyChanged = Math.abs(shortOption.qty) !== Math.abs(memento.orderQty);

        if (baseQtyChanged) {

            desiredState['ShortOption'] = {
                qty: memento.orderQty * -1,
                strike: shortOption.strike,
                expiration: shortOption.selectedExpiration.optionExpirationDate
            };
        }

        // Spread Long Leg
        const spreadLong = beforeState.find(x => x.role === 'SpreadLongLeg');

        if (baseQtyChanged) {
            desiredState['SpreadLongLeg'] = {
                qty: memento.orderQty,
                strike: spreadLong.strike,
                expiration: spreadLong.selectedExpiration.optionExpirationDate
            };
        }

        // Spread Short Leg
        const spreadShort = beforeState.find(x => x.role === 'SpreadShortLeg');

        if (baseQtyChanged) {
            desiredState['SpreadShortLeg'] = {
                qty: memento.orderQty * -1,
                strike: spreadShort.strike,
                expiration: spreadShort.selectedExpiration.optionExpirationDate
            };
        }

        // 2nd Spread Long Leg
        let secondSpreadLongLeg = beforeState.find(x => x.role === 'SecondSpreadLongLeg');
        let secondSpreadShortLeg = beforeState.find(x => x.role === 'SecondSpreadShortLeg');

        let secondSpreadLongLegNew: SolutionPositionDto;
        let secondSpreadShortLegNew: SolutionPositionDto;

        if (!isVoid(secondSpreadLongLeg)) {
            if (memento.hasSecondSpread) {
                desiredState['SecondSpreadLongLeg'] = {
                    qty: memento.orderQty,
                    strike: secondSpreadLongLeg.strike,
                    expiration: secondSpreadLongLeg.selectedExpiration.optionExpirationDate
                };
                desiredState['SecondSpreadShortLeg'] = {
                    qty: memento.orderQty,
                    strike: secondSpreadShortLeg.strike,
                    expiration: secondSpreadShortLeg.selectedExpiration.optionExpirationDate
                };
            } else {
                secondSpreadLongLeg = undefined;
                secondSpreadShortLeg = undefined;

            }
        } else {

            if (memento.hasSecondSpread) {

                const defaults = this._positionsStateService.getDefaultsProvider(
                    this.selectedPortfolio.id,
                    this.settingsSection.globalSettings.underlying,
                    'double'
                );

                const chain = await this._optionChainsService
                    .getChain(this.settingsSection.globalSettings.underlying);

                const exp = chain.expirations
                    .find(x => x.daysToExpiration >= defaults.first.secondSpreadDaysToExpiration);

                const multiplier = 1;

                let longLegStrike = isReversedCashFlowOrder(spreadShort.strategy)
                    ? spreadShort.strike + (defaults.first.secondSpreadOffset || 0) * multiplier
                    : spreadShort.strike - (defaults.first.secondSpreadOffset || 0) * multiplier;

                let shortLegStrike = isReversedCashFlowOrder(spreadShort.strategy)
                    ? longLegStrike + (defaults.first.secondSpreadWidth || 0) * multiplier
                    : longLegStrike - (defaults.first.secondSpreadWidth || 0) * multiplier;

                if (exp.strikes.indexOf(longLegStrike) < 0) {
                    longLegStrike = null;
                }

                const longLegTicker = makeOptionTicker(exp, spreadShort.type, longLegStrike, 'American');

                const longLegPosition: SolutionPositionDto = {
                    ticker: longLegTicker,
                    side: 'Long',
                    qty: memento.orderQty,
                    underlying: spreadLong.underlyingSymbol,
                    type: spreadLong.type,
                    strike: longLegStrike,
                    expiration: makeGuiFriendlyExpirationDate(exp.optionExpirationDate),
                    role: 'SecondSpreadLongLeg',
                    strategy: spreadLong.strategy,
                    priceMultiplier: Math.sign(spreadLong.qty) * -1,
                    expirationFull: makeFullExpirationDate(exp.optionExpirationDate)
                };

                secondSpreadLongLegNew = longLegPosition;

                const shortLegTicker = makeOptionTicker(exp, spreadShort.type, shortLegStrike, 'American');

                const shortLegPosition: SolutionPositionDto = {
                    ticker: shortLegTicker,
                    side: 'Short',
                    qty: memento.orderQty * -1,
                    underlying: spreadShort.underlyingSymbol,
                    type: spreadShort.type,
                    strike: shortLegStrike,
                    expiration: makeGuiFriendlyExpirationDate(exp.optionExpirationDate),
                    role: 'SecondSpreadShortLeg',
                    strategy: spreadShort.strategy,
                    priceMultiplier: Math.sign(spreadShort.qty) * -1,
                    expirationFull: makeFullExpirationDate(exp.optionExpirationDate)
                };

                secondSpreadShortLegNew = shortLegPosition;
            }
        }


        // Protective Option Leg
        const poLeg = beforeState.find(x => x.role === 'ProtectiveOption');

        let poLegStrike: number;

        if (!isVoid(secondSpreadShortLegNew)) {
            poLegStrike = secondSpreadShortLegNew.strike
        } else {
            const hadSecondSpread = beforeState.some(x => x.role === 'SecondSpreadShortLeg');
            if (isVoid(secondSpreadShortLeg) && hadSecondSpread) {
                poLegStrike = spreadShort.strike;
            } else {
                poLegStrike = poLeg.strike;
            }
        }

        if (memento.protectiveOptionOverrideQty) {

            desiredState['ProtectiveOption'] = {
                qty: memento.protectiveOptionOverrideQty,
                strike: poLegStrike,
                expiration: poLeg.selectedExpiration.optionExpirationDate
            };

        } else {
            desiredState['ProtectiveOption'] = {
                qty: memento.orderQty,
                strike: poLegStrike,
                expiration: poLeg.selectedExpiration.optionExpirationDate
            };
        }

        let secondPoLeg = beforeState.find(x => x.role === 'SecondProtectiveOption');

        let secondPoLegNew: SolutionPositionDto;

        if (!isVoid(secondPoLeg)) {

            let secondPoLegStrike: number;

            const hadSecondSpread = beforeState.some(x => x.role === 'SecondSpreadShortLeg');

            if (isVoid(secondSpreadShortLegNew) && !hadSecondSpread) {
                secondPoLegStrike = secondPoLeg.strike;
            } else {
                secondPoLegStrike = isReversedCashFlowOrder(secondPoLeg.strategy)
                    ? poLegStrike + (defaults.first.secondProtectiveOptionOffset || 0)
                    : poLegStrike - (defaults.first.secondProtectiveOptionOffset || 0);
            }

            if (memento.hasSecondPo) {
                if (memento.secondProtectiveOptionOverrideQty) {
                    desiredState['SecondProtectiveOption'] = {
                        qty: memento.secondProtectiveOptionOverrideQty,
                        strike: secondPoLegStrike,
                        expiration: secondPoLeg.selectedExpiration.optionExpirationDate
                    };
                } else {
                    desiredState['SecondProtectiveOption'] = {
                        qty: memento.orderQty,
                        strike: secondPoLegStrike,
                        expiration: secondPoLeg.selectedExpiration.optionExpirationDate
                    };
                }
            } else {
                secondPoLeg = undefined;
            }
        } else {

            if (memento.hasSecondPo) {

                const chain = await this._optionChainsService
                    .getChain(this.settingsSection.globalSettings.underlying);

                const exp = chain.expirations
                    .find(x => x.daysToExpiration >= defaults.first.secondProtectiveOptionDaysToExpiration);

                const multiplier = 1;

                let strike = isReversedCashFlowOrder(poLeg.strategy)
                    ? poLegStrike + (defaults.first.secondProtectiveOptionOffset || 0) * multiplier
                    : poLegStrike - (defaults.first.secondProtectiveOptionOffset || 0) * multiplier;

                if (exp.strikes.indexOf(strike) < 0) {
                    strike = null;
                }

                const ticker = makeOptionTicker(exp, poLeg.type, strike, 'American');

                const p: SolutionPositionDto = {
                    ticker: ticker,
                    side: poLeg.side,
                    qty: (memento.secondProtectiveOptionOverrideQty || memento.orderQty) * Math.sign(poLeg.qty),
                    underlying: poLeg.underlyingSymbol,
                    type: poLeg.type,
                    strike: strike,
                    expiration: makeGuiFriendlyExpirationDate(exp.optionExpirationDate),
                    role: 'SecondProtectiveOption',
                    strategy: poLeg.strategy,
                    priceMultiplier: Math.sign(poLeg.qty) * -1,
                    expirationFull: makeFullExpirationDate(exp.optionExpirationDate)
                };

                secondPoLegNew = p;
            }
        }

        let overrideState = desiredState['ShortOption'];
        let afterLeg = this.makeAfterLeg(shortOption, overrideState);
        result.push(afterLeg);

        overrideState = desiredState['SpreadLongLeg'];
        afterLeg = this.makeAfterLeg(spreadLong, overrideState);
        result.push(afterLeg);

        overrideState = desiredState['SpreadShortLeg'];
        afterLeg = this.makeAfterLeg(spreadShort, overrideState);
        result.push(afterLeg);

        if (secondSpreadLongLeg) {
            overrideState = desiredState['SecondSpreadLongLeg'];
            afterLeg = this.makeAfterLeg(secondSpreadLongLeg, overrideState);
            result.push(afterLeg);

            overrideState = desiredState['SecondSpreadShortLeg'];
            afterLeg = this.makeAfterLeg(secondSpreadShortLeg, overrideState);
            result.push(afterLeg);

        } else {

            if (!isVoid(secondSpreadLongLegNew) && !isVoid(secondSpreadShortLegNew)) {
                result.push(secondSpreadLongLegNew);
                result.push(secondSpreadShortLegNew);
            }

        }

        overrideState = desiredState['ProtectiveOption'];
        afterLeg = this.makeAfterLeg(poLeg, overrideState);
        result.push(afterLeg);

        if (secondPoLeg) {
            overrideState = desiredState['SecondProtectiveOption'];
            afterLeg = this.makeAfterLeg(secondPoLeg, overrideState);
            result.push(afterLeg);
        } else {
            if (secondPoLegNew) {
                result.push(secondPoLegNew);
            }
        }

        if (isReversedCashFlowOrder(shortOption.strategy)) {
            result.reverse();
        }

        return result;
    }

    private makeAfterLeg(leg: BeforePositionModel, override: DesiredMementoState): SolutionPositionDto {

        const expiration = isVoid(override)
            ? makeGuiFriendlyExpirationDate(leg.selectedExpiration.optionExpirationDate)
            : makeGuiFriendlyExpirationDate(override.expiration);

        const expirationFull = makeFullExpirationDate(expiration);

        const qty = isVoid(override)
            ? leg.qty
            : override.qty;

        const strike = isVoid(override)
            ? leg.strike
            : override.strike;

        const p: SolutionPositionDto = {
            ticker: leg.getTicker(),
            side: leg.side,
            qty: qty,
            underlying: leg.underlyingSymbol,
            type: leg.type,
            strike: strike,
            expiration: expiration,
            role: leg.role,
            strategy: leg.strategy,
            priceMultiplier: Math.sign(leg.qty) * -1,
            expirationFull: expirationFull
        };

        return p;
    }

    private makeOrdersForPositionAdjustment(beforeState: BeforePositionModel[], afterState: SolutionPositionDto[]): SolutionOrderLegDto[] {

        const roles: CashFlowStrategyRole[] = [
            'ShortOption',
            'SpreadLongLeg',
            'SpreadShortLeg',
            'SecondSpreadLongLeg',
            'SecondSpreadShortLeg',
            'ProtectiveOption',
            'SecondProtectiveOption'
        ];

        const pairs = roles.map(role => {
            const before = beforeState.find(x => x.role === role);
            const after = afterState.find(x => x.role === role);

            return {before, after};
        });


        const orders = pairs.flatMap(pair => {

            if (isVoid(pair.before) && isVoid(pair.after)) {
                return undefined;
            }

            if (isVoid(pair.before)) {

                const order: SolutionOrderLegDto = {
                    ticker: pair.after.ticker,
                    qty: pair.after.qty,
                    underlying: pair.after.underlying,
                    type: pair.after.type,
                    strike: isVoid(pair.after.strike) ? null : pair.after.strike.toString(),
                    expiration: pair.after.expiration,
                    role: pair.after.role,
                    action: pair.after.qty < 0 ? 'Sell To Open' : 'Buy To Open'
                };

                return order;

            }

            if (isVoid(pair.after)) {

                const order: SolutionOrderLegDto = {
                    ticker: pair.before.getTicker(),
                    qty: pair.before.qty * -1,
                    underlying: pair.before.underlyingSymbol,
                    type: pair.before.type,
                    strike: isVoid(pair.before.strike) ? null : pair.before.strike.toString(),
                    expiration: makeGuiFriendlyExpirationDate(pair.before.selectedExpiration.optionExpirationDate),
                    role: pair.before.role,
                    action: pair.before.qty < 0 ? 'Buy To Close' : 'Sell To Close'
                };

                return order;

            }

            if (pair.after.ticker !== pair.before.getTicker()) {

                const closing: SolutionOrderLegDto = {
                    ticker: pair.before.getTicker(),
                    qty: pair.before.qty * -1,
                    underlying: pair.before.underlyingSymbol,
                    type: pair.before.type,
                    strike: pair.before.strike + '',
                    expiration: makeGuiFriendlyExpirationDate(pair.before.selectedExpiration.optionExpirationDate),
                    role: pair.before.role,
                    action: pair.before.qty > 0 ? 'Sell To Close' : 'Buy To Close'
                };

                const opening: SolutionOrderLegDto = {
                    ticker: pair.after.ticker,
                    qty: pair.after.qty,
                    underlying: pair.after.underlying,
                    type: pair.after.type,
                    strike: pair.after.strike + '',
                    expiration: pair.after.expiration,
                    role: pair.after.role,
                    action: pair.before.qty > 0 ? 'Buy To Open' : 'Sell To Open'
                }

                return [opening, closing];

            }

            const ticker = pair.after.ticker;

            const qty = pair.after.qty - pair.before.qty;

            if (Math.abs(qty) === 0) {
                return undefined;
            }

            const underlying = pair.after.underlying;
            const type = pair.after.type;
            const strike = isVoid(pair.after.strike) ? null : pair.after.strike.toString();
            const expiration = pair.after.expiration;
            const role = pair.after.role;
            const action: SolutionOrderLegActions = qty < 0 ? 'Sell To Close' : 'Buy To Open';

            const diffOrder: SolutionOrderLegDto = {
                ticker,
                qty,
                underlying,
                type,
                strike,
                expiration,
                role,
                action
            };

            return diffOrder;

        }).filter(x => !isVoid(x));

        return orders;
    }

    private async onAfterStateDetalizationReady() {
        const data = this._afterStateDetalizationService.getData();
        await this.afterStateDetalizationDialog.show(data);
        this._afterStateDetalizationService.resolve();
    }

    private markChangedPositions(beforeState: SolutionPositionDto[], afterState: SolutionPositionDto[]) {

        const roles: CashFlowStrategyRole[] = [
            'ShortOption',
            'SpreadLongLeg',
            'SpreadShortLeg',
            'SecondSpreadLongLeg',
            'SecondSpreadShortLeg',
            'ProtectiveOption',
            'SecondProtectiveOption'
        ];

        const pairs = roles.map(role => {
            const before = beforeState.find(x => x.role === role);
            const after = afterState.find(x => x.role === role);

            return {before, after};
        });

        const secondSpreadBefore = beforeState.some(x => x.role.startsWith('SecondSpread'));
        const secondSpreadAfter = afterState.some(x => x.role.startsWith('SecondSpread'));

        const secondSpreadChanged = (secondSpreadBefore && !secondSpreadAfter)
            || (!secondSpreadBefore && secondSpreadAfter);

        pairs.forEach(p => {
            if (isVoid(p.before) && isVoid(p.after)) {
                return;
            }

            if (isVoid(p.after)) {
                return;
            }

            if (isVoid(p.before)) {
                p.after.changing = true;
                return;
            }

            if (p.before.ticker !== p.after.ticker) {
                p.after.changing = true;
            }

            const isProtective = p.before.role.indexOf('Protective') >= 0
                || p.after.role.indexOf('Protective') >= 0;

            if (isProtective && secondSpreadChanged) {
                p.after.changing = true;
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    async loadDataZonesGrid(row?: PriceboxRow) {

        if (isVoid(this.zonesGridSettingsDialog)) {
            return;
        }

        if (isVoid(this.zonesGridSection)) {
            return;
        }

        let zgs: ZonesGridSettings = this.zonesGridSettingsDialog.getSettings(this.settingsSection.underlying);

        let restoreZonesGrid = true;

        if (!isVoid(row)) {
            try {
                zgs = await this.zonesGridSettingsDialog.show(this.settingsSection.underlying);
            } catch {
                return;
            }
        } else {
            restoreZonesGrid = false;
            row = this.zonesGridSection.loadedRow;
        }

        const adjustmentPricingSettings = await this.getAdjustmentPricingSettingsObject('Zones Grid');

        if (!isVoid(adjustmentPricingSettings.errors)) {
            adjustmentPricingSettings.errors.forEach(x => this._toastr.error(x));
            return;
        }

        this.resetZonesGrid();

        if (restoreZonesGrid) {
            this.zonesGridColCollapsed = false;
        }

        let logicalId: any = row.columns[0].adjustments[0].logicalId;
        if (row.columns[0].adjustments.length > 1) {
            logicalId = logicalId + '+' + row.columns[0].adjustments[1].logicalId;
        }

        adjustmentPricingSettings.settings.forEach(x => {
            x.zonesGridRange = zgs.range;
            x.zonesGridRangeStep = zgs.step;
            x.zonesGridAdjustment = logicalId;
            x.zonesGridRangeDown = zgs.rangeDown;
        });

        const requestId = 'zones_' + getShortUUID();

        this._pendingRequestId.push(requestId);

        const qry = new GetAdjustmentSolutions(
            requestId,
            adjustmentPricingSettings.settings,
        );

        this.isDataLoading = true;

        if (this.settingsSection.globalSettings.useTheoreticalPrices) {
            const theoreticalPriceIv = this.settingsSection.globalSettings.theoreticalPriceIv;

            let volatilities = `total=${theoreticalPriceIv}`;

            const cmd = new SetImpliedVolatilityShell(
                this.settingsSection.underlying,
                volatilities
            );

            await this._shellClient.processCommand(cmd);
        }

        try {

            await this._shellClient.processCommand(qry);

            this.zonesGridSection.loadedRow = row;

            if (!isVoid(adjustmentPricingSettings)) {
                if (!isVoid(adjustmentPricingSettings.settings)) {
                    this.zonesGridSection.lastUsedSettings = adjustmentPricingSettings.settings;
                }
            }

        } catch (e) {
            removeFromArray(this._pendingRequestId, requestId);
            this._toastr.error('"Load Zones Grid Pricing Data" operation completed with errors');
            this.isDataLoading = false;
        }
    }

    showPricingStepsPopup(column: PriceboxColumn): void {
        if (isVoid(column) || isVoid(column.adjustments) || column.adjustments.length === 0) {
            return;
        }

        const underlying = this.settingsSection.underlying;

        const savedSettings = this._userSettingsService
            .getValue<{ numOfSteps?: number, stepSize?: number }>(`apg-pricing-steps.${underlying}.settings`);

        let stepSize: number;
        let numSteps: number;

        if (!isVoid(savedSettings)) {
            stepSize = isValidNumber(savedSettings.stepSize, true) ? savedSettings.stepSize : null;
            numSteps = isValidNumber(savedSettings.numOfSteps, true) ? savedSettings.numOfSteps : null;
        }

        if (!isValidNumber(stepSize, true)) {
            const zgs = this.zonesGridSettingsDialog?.getSettings(underlying);
            if (!isVoid(zgs) && isValidNumber(zgs.step, true)) {
                stepSize = zgs.step;
            } else {
                stepSize = 10;
            }
        }

        if (!isValidNumber(numSteps, true)) {
            numSteps = 5;
        }

        let logicalId: any = column.adjustments[0].logicalId;
        if (column.adjustments.length > 1) {
            logicalId = logicalId + '+' + column.adjustments[1].logicalId;
        }

        const adjustmentType = Enumerable.from(column.adjustments)
            .select(x => x.adjustmentType)
            .distinct()
            .toJoinedString('+');

        const expirationFull = `${column.dayOfWeek} ${column.expirationDatePrettyWithDaysToExpiration}`;

        const config: ApgPricingStepsConfig = {
            logicalId,
            adjustmentType,
            expiration: column.expirationDatePretty,
            expirationFull,
            underlying,
            stepSize,
            numSteps,
        };

        this.apgPricingStepsPopup.show(config);

        this.loadPricingStepsData(config);
    }

    async loadPricingStepsData(config: ApgPricingStepsConfig): Promise<void> {
        const adjustmentPricingSettings = await this.getAdjustmentPricingSettingsObject('Zones Grid');

        if (!isVoid(adjustmentPricingSettings.errors)) {
            adjustmentPricingSettings.errors.forEach(x => this._toastr.error(x));
            this.apgPricingStepsPopup.close();
            return;
        }

        const numSteps = config.numSteps || 5;
        const baseRange = numSteps * config.stepSize;

        adjustmentPricingSettings.settings.forEach(x => {
            x.zonesGridRange = baseRange;
            x.zonesGridRangeDown = baseRange;
            x.zonesGridRangeStep = config.stepSize;
            x.zonesGridAdjustment = config.logicalId;
            x.zonesGridRangeOffset = 0;
        });

        const requestId = 'pricingSteps_' + getShortUUID();
        this._pendingRequestId.push(requestId);

        const qry = new GetAdjustmentSolutions(
            requestId,
            adjustmentPricingSettings.settings,
        );

        try {
            await this._shellClient.processCommand(qry);
        } catch (e) {
            removeFromArray(this._pendingRequestId, requestId);
            this.apgPricingStepsPopup.close();
            this._toastr.error('"Pricing Steps" operation completed with errors');
        }
    }

    resetPricingStepsSubscriptions(): void {
        this._strategyCodeIndex.resetPricingSteps();
    }

    isPriceBoxTheoretical(): boolean {
        return this._strategyCodeIndex.isPriceBoxTheoretical();
    }

    isZonesGridTheoretical(): boolean {
        return this._strategyCodeIndex.isZonesGridTheoretical();
    }

    private fillTheBlanks(adjustments: CashFlowAdjustment[], isZonesData: boolean) {
        if (this.settingsSection.globalSettings.theoreticalPriceMode !== 'Fill Blanks') {
            return;
        }

        const target = this.settingsSection.globalSettings.theoreticalPriceTarget;
        if (target !== 'All') {
            if (isZonesData && target !== 'Zones Grid') {
                return;
            }

            if (!isZonesData && target !== 'Price Box') {
                return;
            }
        }

        const blankAdjustments = adjustments
            .filter(x => !x.isEmpty)
            .filter(x => isVoid(x.price));

        console.log(`found ${blankAdjustments.length} empty adjustments`);

        if (blankAdjustments.length === 0) {
            this._toastr.info('No blank adjustments found', 'Theoretical Prices - "Fill Blanks"');
            return;
        }

        this._toastr.info(`${blankAdjustments.length} blank adjustments found. Replacing missing quotes with theoretical prices...`
            , 'Theoretical Prices - "Fill Blanks"');

        blankAdjustments.forEach(adj => {

            let originalStrategyCode = adj.optionStrategyCode;
            let updatedStrategyCode = originalStrategyCode;

            console.group(`processing blank adjustment: ${adj.uniqueAdjustmentSpecificationId}`);

            console.log(`original strategy code: ${originalStrategyCode}`);

            const replaced: { [ix: string]: string } = {};

            function updateOrderBlanks(legs: SolutionOrderLegDto[]) {
                legs.forEach(leg => {

                    if (!isVoid(leg.price)) {
                        return;
                    }

                    console.log(`detected blank leg: ticker=${leg.ticker}`);

                    const legOriginalTicker = leg.ticker;

                    const legTheoTicker = leg.ticker + ' !0';

                    updatedStrategyCode = updatedStrategyCode.replace(legOriginalTicker, legTheoTicker);

                    console.log(`updated strategy code: ${updatedStrategyCode}`);

                    leg.ticker = legTheoTicker;

                    replaced[legOriginalTicker] = legTheoTicker;
                });
            }

            updateOrderBlanks(adj.mainLegs);
            updateOrderBlanks(adj.linkedLegs);
            updateOrderBlanks(adj.secondLinkedLegs);
            updateOrderBlanks(adj.thirdLinkedLegs);

            function updatePositionBlanks(poses: SolutionPositionDto[]) {
                poses.forEach(pos => {
                    const replaceTo = replaced[pos.ticker];
                    if (isVoid(replaceTo)) {
                        return;
                    }
                    pos.ticker = replaceTo;
                });
            }

            updatePositionBlanks(adj.beforeState);
            updatePositionBlanks(adj.afterState);

            adj.optionStrategyCode = updatedStrategyCode;

            console.log(`final strategy code: ${updatedStrategyCode}`);

            console.groupEnd();
        });

        if (!isZonesData) {
            this._quoteIndex.addData(blankAdjustments);
            this._strategyCodeIndex.addData(blankAdjustments);
        } else {
            this._strategyCodeIndex.addDataZones(blankAdjustments);
            this._quoteIndex.addDataZones(blankAdjustments);
        }

        this._toastr.info('Blank adjustments are filled with theoretical prices', 'Theoretical Prices - "Fill Blanks"');
    }

    getPriceBoxSectionHeaderVolatility() {
        return this._priceboxSectionHeaderVolatility;
    }

    getZonesGridSectionHeaderVolatility() {
        return this._zonesGridSectionHeaderVolatility;
    }

    @DetectMethodChanges()
    zonesGridChangeLinkState() {
        this.isZonesGridLinked = !this.isZonesGridLinked;
    }

    zonesGridClear() {
        this.resetZonesGrid();
    }

    areSettingsDifferent(): boolean {
        if (isVoid(this.zonesGridSection)) {
            return false;
        }

        if (isVoid(this.priceboxSection.lastUsedSettings)) {
            return false;
        }

        if (isVoid(this.priceboxSection.rows)) {
            return false;
        }

        if (isVoid(this.zonesGridSection.lastUsedSettings)) {
            return false;
        }

        if (isVoid(this.zonesGridSection.rows)) {
            return false;
        }

        const pb = JSON.stringify(this.priceboxSection.lastUsedSettings || '{}');

        const zg = JSON.stringify(this.zonesGridSection.lastUsedSettings || '{}');

        return pb !== zg;
    }

    getPriceboxTableWidth() {
        let width = this.getPriceboxSectionWidth();
        return width + '%';
    }

    getPriceboxHeaderWidth() {
        return this.getPriceboxSectionWidth();
    }

    private getPriceboxSectionWidth(): number {
        let width = 100;
        if (this.priceboxSection.showZonesGridArrow) {
            width = 99;
            if (!this.zonesGridColCollapsed) {
                width = 98;
            }
        }

        return width;
    }

    @DetectMethodChanges()
    toggleShowHedges() {
        this.showHedges = !this.showHedges;
    }

    private async onQuoteGeneratorQuote(payload: { quote: QuoteGeneratorQuoteDto }) {

        if (isVoid(payload)) {
            return;
        }

        if (!this._sessionService.loginResult.isQuoteGeneratorShell) {
            return;
        }

        if (payload?.quote?.ticker.startsWith("@")) {
            return;
        }

        this._isWaitingAtmChangeEvent = true;
    }
}
